/**
 * PropertyDetailsForm Component
 * 
 * Form component for editing global property definitions with multiple tabs.
 * Extracted from GlobalPropertyTableView for better separation of concerns.
 * 
 * NOTE: This is a large component containing ~800+ lines of JSX. The full implementation
 * with all tabs and form fields will be completed during integration with the main component.
 */

import React, { useEffect, useState } from 'react';
import {
  Form,
  Tabs,
  Input,
  Select,
  Switch,
  InputNumber,
  Button,
  Space,
  Divider,
  Alert,
  Card,
  Row,
  Col,
  Table,
  Tag,
  Typography,
  Descriptions,
  message,
  Cascader,
  Radio,
  Checkbox,
  DatePicker
} from 'antd';
import { apiClient } from '../../services/apiClient';
import {
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';
import {
  ChangeTypeDefinition,
  StateActions,
  TransitionChangeReference,
  CalculationDependencies,
  ValidationRules,
  ReferenceConfiguration,
  getChangeTypeName,
  isNewChangeTypeStructure
} from '../../types/globalProperties';
import { getAvailableNamedConfigurations } from '../../utils/globalPropertyUtils';
import { PROPERTY_TYPES, CALCULATION_TYPES, FUNCTION_TYPES, SOURCE_TYPES, KRYSA_USER_INPUT_TYPES, KRYSA_USER_OUTPUT_FIELDS, INPUT_MASK_TYPES } from '../../constants/globalPropertyConstants';
import dayjs from 'dayjs';
import { isNumericPropertyType, getOperatorOptions } from '../../utils/globalPropertyUtils';
import { PropertyKeySelector } from './PropertyKeySelector';
import { StateFlowEditor } from './StateFlowEditor';
import { skillService, SkillTreeNode } from '../../services/skillService';
import { ArtifactTypeSelector } from '../common/Artifact/ArtifactTypeSelector';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

// Resource Tab Content Component
interface ResourceTabContentProps {
  form: FormInstance;
  validationRules?: ValidationRules | Record<string, any>;
}

// Validation Tab Content Component
interface ValidationTabContentProps {
  form: FormInstance;
  validationRules?: ValidationRules | Record<string, any>;
  propertyType?: string;
}

// Reference Tab Content Component
interface ReferenceTabContentProps {
  form: FormInstance;
  calculationDependencies?: CalculationDependencies | Record<string, any>;
}

// Calculation Tab Content Component
interface CalculationTabContentProps {
  form: FormInstance;
  propertyType?: string;
  calculationDependencies?: CalculationDependencies | Record<string, any>;
}

// Condition Tab Content Component
interface ConditionTabContentProps {
  form: FormInstance;
  validationRules?: ValidationRules | Record<string, any>;
}

interface SkillCascaderOption {
  value: string;
  label: string;
  skill_level?: number;
  children?: SkillCascaderOption[];
  isLeaf?: boolean;
}

// Generate Date Range Config Component - manages its own state for mode selection
interface GenerateDateRangeConfigProps {
  calculation: { key: string; config: any };
  handleCalculationChange: (key: string, field: string, value: any) => void;
}

const GenerateDateRangeConfig: React.FC<GenerateDateRangeConfigProps> = ({ calculation, handleCalculationChange }) => {
  const funcParams = calculation.config?.function_parameters || {};
  
  // Determine initial mode from data
  const getInitialMode = React.useCallback(() => {
    const hasStaticDate = funcParams.reference_date && funcParams.reference_date.trim() !== '';
    const hasPropertyRef = funcParams.reference_date_property && funcParams.reference_date_property.trim() !== '';
    // If property ref has a value, use property mode; otherwise static
    if (hasPropertyRef) return 'property';
    return 'static';
  }, [funcParams.reference_date, funcParams.reference_date_property]);
  
  // Use local state for mode - this prevents the radio from being controlled by empty string checks
  const [mode, setMode] = React.useState<'static' | 'property'>(getInitialMode);
  
  // Update mode when calculation config changes (e.g., when loading a different property)
  React.useEffect(() => {
    setMode(getInitialMode());
  }, [calculation.key, getInitialMode]);
  
  // Helper to build source_properties from selected property keys
  const buildSourceProperties = (refDateProp?: string, rangeIntervalProp?: string) => {
    const sourceProps: string[] = [];
    if (refDateProp) sourceProps.push(refDateProp);
    if (rangeIntervalProp) sourceProps.push(rangeIntervalProp);
    return sourceProps;
  };

  const hasStaticDate = funcParams.reference_date && funcParams.reference_date.trim() !== '';
  const hasPropertyRef = funcParams.reference_date_property && funcParams.reference_date_property.trim() !== '';
  const rangeIntervalProperty = funcParams.range_interval_property || '';

  return (
    <>
      <Divider orientation="left">Date Range Parameters</Divider>
      <Alert
        message="Generate Date Range"
        description="Automatically generates sequential date ranges for artifact items. Reference the properties to use for reference date and range interval. Define where these properties are sourced in the References tab."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      {/* Reference Date Mode Selection */}
      <Form.Item label="Reference Date Source">
        <Radio.Group
          value={mode}
          onChange={(e) => {
            const newMode = e.target.value as 'static' | 'property';
            setMode(newMode);
            
            const newParams: Record<string, any> = {};
            
            // Always preserve range_interval_property if set
            if (rangeIntervalProperty) {
              newParams.range_interval_property = rangeIntervalProperty;
            }
            
            if (newMode === 'static') {
              // Switch to static date mode - only set reference_date
              newParams.reference_date = funcParams.reference_date || '';
              // Don't include reference_date_property
            } else {
              // Switch to property reference mode - only set reference_date_property
              newParams.reference_date_property = funcParams.reference_date_property || '';
              // Don't include reference_date
            }
            
            handleCalculationChange(calculation.key, 'function_parameters', newParams);
            // Update source_properties
            const refDatePropForSource = newMode === 'property' ? (newParams.reference_date_property || '') : '';
            handleCalculationChange(calculation.key, 'source_properties', 
              buildSourceProperties(refDatePropForSource || undefined, rangeIntervalProperty || undefined));
          }}
        >
          <Radio value="static">Static Date</Radio>
          <Radio value="property">From Property (date type)</Radio>
        </Radio.Group>
      </Form.Item>

      {/* Static Date Picker - shown when in static mode */}
      {mode === 'static' && (
        <Form.Item 
          label="Reference Date"
          help="The starting point for generating date ranges (ISO format: YYYY-MM-DD)"
        >
          <DatePicker
            value={funcParams.reference_date ? dayjs(funcParams.reference_date) : null}
            onChange={(date) => {
              const newParams: Record<string, any> = {
                reference_date: date ? date.format('YYYY-MM-DD') : ''
              };
              // Preserve range_interval_property if set
              if (rangeIntervalProperty) {
                newParams.range_interval_property = rangeIntervalProperty;
              }
              handleCalculationChange(calculation.key, 'function_parameters', newParams);
            }}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
            placeholder="Select reference date"
          />
        </Form.Item>
      )}

      {/* Property Reference Selector - shown when in property mode */}
      {mode === 'property' && (
        <Form.Item 
          label="Reference Date Property"
          help="Select a date property whose value will be used as the reference date"
        >
          <PropertyKeySelector
            value={funcParams.reference_date_property || ''}
            onChange={(value) => {
              const propKey = typeof value === 'string' ? value : value[0] || '';
              const newParams: Record<string, any> = {
                reference_date_property: propKey
              };
              // Preserve range_interval_property if set
              if (rangeIntervalProperty) {
                newParams.range_interval_property = rangeIntervalProperty;
              }
              handleCalculationChange(calculation.key, 'function_parameters', newParams);
              // Update source_properties
              handleCalculationChange(calculation.key, 'source_properties', 
                buildSourceProperties(propKey || undefined, rangeIntervalProperty || undefined));
            }}
            filterByType="date"
            placeholder="Select a date property"
          />
        </Form.Item>
      )}

      {/* Range Interval Property Selection */}
      <Form.Item 
        label="Range Interval Property"
        help="Select a list-type property that defines the interval options (week, month, quarter, etc.)"
      >
        <PropertyKeySelector
          value={rangeIntervalProperty}
          onChange={(value) => {
            const propKey = typeof value === 'string' ? value : value[0] || '';
            const newParams: Record<string, any> = {};
            newParams.range_interval_property = propKey;
            // Keep the relevant reference date field
            if (mode === 'static') {
              if (funcParams.reference_date) {
                newParams.reference_date = funcParams.reference_date;
              }
            } else {
              if (funcParams.reference_date_property) {
                newParams.reference_date_property = funcParams.reference_date_property;
              }
            }
            handleCalculationChange(calculation.key, 'function_parameters', newParams);
            // Update source_properties
            const refDateProp = mode === 'property' ? funcParams.reference_date_property : undefined;
            handleCalculationChange(calculation.key, 'source_properties', 
              buildSourceProperties(refDateProp || undefined, propKey || undefined));
          }}
          filterByType="list"
          placeholder="Select a list property for interval"
        />
      </Form.Item>

      {/* Info about References tab */}
      <Alert
        message="Configure References"
        description="The selected property keys should be configured in the References tab to define where their values are sourced from (same artifact, parent, child, etc.)."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Validation Warning */}
      {!hasStaticDate && !hasPropertyRef && (
        <Alert
          message="Reference Date Required"
          description="Please provide either a static reference date or select a date property to use as the reference."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
    </>
  );
};

// Generate Improvement Effective Date Range Config Component
interface GenerateImprovementEffectiveDateRangeConfigProps {
  calculation: { key: string; config: any };
  handleCalculationChange: (key: string, field: string, value: any) => void;
}

const GenerateImprovementEffectiveDateRangeConfig: React.FC<GenerateImprovementEffectiveDateRangeConfigProps> = ({ calculation, handleCalculationChange }) => {
  const funcParams = calculation.config?.function_parameters || {};
  
  // Track if override dates are enabled
  const [useStartDateOverride, setUseStartDateOverride] = React.useState(!!funcParams.start_date);
  const [useEndDateOverride, setUseEndDateOverride] = React.useState(!!funcParams.end_date);
  
  // Update local state when calculation changes
  React.useEffect(() => {
    setUseStartDateOverride(!!funcParams.start_date);
    setUseEndDateOverride(!!funcParams.end_date);
  }, [calculation.key, funcParams.start_date, funcParams.end_date]);
  
  const updateFunctionParams = (updates: Record<string, any>) => {
    const newParams = { ...funcParams, ...updates };
    // Remove undefined/null values
    Object.keys(newParams).forEach(key => {
      if (newParams[key] === undefined || newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });
    handleCalculationChange(calculation.key, 'function_parameters', newParams);
  };

  return (
    <>
      <Divider orientation="left">Improvement Effective Date Range Parameters</Divider>
      <Alert
        message="Improvement Effective Date Range"
        description="This function generates the effective date range for an improvement artifact item. It is called automatically when an improvement transitions to the VALIDATED state. The start date defaults to the current date (transition date), and the end date defaults to NULL (indicating an ongoing improvement)."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Start Date Override */}
      <Form.Item label="Override Start Date">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Checkbox
            checked={useStartDateOverride}
            onChange={(e) => {
              setUseStartDateOverride(e.target.checked);
              if (!e.target.checked) {
                updateFunctionParams({ start_date: undefined });
              }
            }}
          >
            Use custom start date instead of current date
          </Checkbox>
          {useStartDateOverride && (
            <DatePicker
              value={funcParams.start_date ? dayjs(funcParams.start_date) : null}
              onChange={(date) => {
                updateFunctionParams({ start_date: date ? date.format('YYYY-MM-DD') : undefined });
              }}
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              placeholder="Select start date override"
            />
          )}
        </Space>
      </Form.Item>

      {/* End Date Override */}
      <Form.Item label="Set End Date">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Checkbox
            checked={useEndDateOverride}
            onChange={(e) => {
              setUseEndDateOverride(e.target.checked);
              if (!e.target.checked) {
                updateFunctionParams({ end_date: undefined });
              }
            }}
          >
            Set an end date (otherwise defaults to NULL for ongoing improvements)
          </Checkbox>
          {useEndDateOverride && (
            <DatePicker
              value={funcParams.end_date ? dayjs(funcParams.end_date) : null}
              onChange={(date) => {
                updateFunctionParams({ end_date: date ? date.format('YYYY-MM-DD') : undefined });
              }}
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              placeholder="Select end date"
            />
          )}
        </Space>
      </Form.Item>

      {/* Default Behavior Info */}
      <Alert
        message="Default Behavior"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><strong>Start Date:</strong> {useStartDateOverride ? `Will use ${funcParams.start_date || 'selected date'}` : 'Will use the current date when the improvement transitions to VALIDATED'}</li>
            <li><strong>End Date:</strong> {useEndDateOverride ? `Will be set to ${funcParams.end_date || 'selected date'}` : 'Will be NULL (indicating an ongoing improvement)'}</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* No Source Properties Needed */}
      <Alert
        message="No Source Properties Required"
        description="This function uses the current date automatically. No source properties need to be configured in the References tab."
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
      />
    </>
  );
};

const ResourceTabContent: React.FC<ResourceTabContentProps> = ({ form, validationRules }) => {
  // Get existing resource configurations from validation_rules
  const resourceConfigs = React.useMemo(() => {
    console.log('[ResourceTabContent] Processing validationRules:', {
      validationRules,
      isObject: typeof validationRules === 'object',
      keys: validationRules ? Object.keys(validationRules) : []
    });
    
    if (!validationRules || typeof validationRules !== 'object') return [];
    const resources: Array<{ key: string; config: any }> = [];
    Object.keys(validationRules).forEach(key => {
      if (key.match(/^resource\d+$/)) {
        resources.push({ key, config: validationRules[key] });
      }
    });
    const sorted = resources.sort((a, b) => {
      const numA = parseInt(a.key.match(/\d+$/)?.[0] || '0', 10);
      const numB = parseInt(b.key.match(/\d+$/)?.[0] || '0', 10);
      return numA - numB;
    });
    console.log('[ResourceTabContent] Extracted resources:', sorted);
    return sorted;
  }, [validationRules]);

  const [localResources, setLocalResources] = useState<Array<{ key: string; config: any }>>(resourceConfigs);
  const [skillOptions, setSkillOptions] = useState<SkillCascaderOption[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  
  // Sync local state with prop changes
  React.useEffect(() => {
    console.log('[ResourceTabContent] Updating localResources from resourceConfigs:', resourceConfigs);
    setLocalResources(resourceConfigs);
  }, [resourceConfigs]);

  // Convert skill tree to cascader options format
  const convertSkillTreeToOptions = React.useCallback((nodes: SkillTreeNode[]): SkillCascaderOption[] => {
    return nodes.map(node => {
      const label = node.name || node.skill_name || 'Unnamed Skill';
      return {
        value: node.id,
        label: label,
        skill_level: node.skill_level,
        isLeaf: !node.children || node.children.length === 0,
        children: node.children && node.children.length > 0 
          ? convertSkillTreeToOptions(node.children)
          : undefined
      };
    });
  }, []);

  // Transform production backend tree response to expected format
  // Handles both {roots: [...]} format and flat object with path keys
  const transformTreeResponse = React.useCallback((data: any): SkillTreeNode[] => {
    if (!data || typeof data !== 'object') {
      return [];
    }

    // Expected format: {roots: SkillTreeNode[], total_count: number}
    if (data.roots && Array.isArray(data.roots)) {
      return data.roots;
    }

    // If it's an array, return as-is (might already be in tree format)
    if (Array.isArray(data)) {
      return data;
    }

    // Production backend returns object with category paths as keys
    // e.g., { "Analytical & Diagnostic > Data Collection": { intermediate: {...}, ... } }
    const rootNodes: SkillTreeNode[] = [];
    const nodeMap = new Map<string, SkillTreeNode>();
    const categoryToNodeMap = new Map<string, SkillTreeNode>();

    // First pass: create all nodes from category paths
    for (const [categoryPath, skillData] of Object.entries(data)) {
      if (!skillData || typeof skillData !== 'object') continue;

      // Handle nested structure (e.g., {intermediate: {...}})
      const actualSkillData = (skillData as any).intermediate || (skillData as any).skill || skillData;
      
      const skillId = actualSkillData.skill_id || actualSkillData.id || categoryPath;
      const skillName = actualSkillData.skill_name || actualSkillData.name || categoryPath.split(' > ').pop() || categoryPath;
      const description = actualSkillData.skill_description || actualSkillData.description;
      const pathParts = categoryPath.split(' > ');
      const skillLevel = actualSkillData.skill_level !== undefined ? actualSkillData.skill_level : pathParts.length - 1;

      const node: SkillTreeNode = {
        id: String(skillId),
        name: String(skillName),
        description: description,
        skill_level: Number(skillLevel),
        parent_ref: undefined,
        children: []
      };

      nodeMap.set(String(skillId), node);
      categoryToNodeMap.set(categoryPath, node);
    }

    // Second pass: build hierarchy from category paths
    const categoryEntries = Array.from(categoryToNodeMap.entries());
    for (const [hierarchyPath, node] of categoryEntries) {
      const pathParts = hierarchyPath.split(' > ');
      
      if (pathParts.length === 1) {
        rootNodes.push(node);
      } else {
        const parentPath = pathParts.slice(0, -1).join(' > ');
        const parentNode = categoryToNodeMap.get(parentPath);
        
        if (parentNode) {
          node.parent_ref = parentNode.id;
          parentNode.children.push(node);
        } else {
          rootNodes.push(node);
        }
      }
    }

    // Sort nodes
    const sortNodes = (nodes: SkillTreeNode[]) => {
      nodes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortNodes(node.children);
        }
      });
    };
    sortNodes(rootNodes);

    return rootNodes;
  }, []);

  // Load skill tree for cascader
  React.useEffect(() => {
    const loadSkillTree = async () => {
      setLoadingSkills(true);
      try {
        // Call the tree endpoint
        const treeResponse = await skillService.getSkillTree();
        console.log('[ResourceTabContent] Raw skill tree response:', treeResponse);
        console.log('[ResourceTabContent] Tree response type:', typeof treeResponse);
        console.log('[ResourceTabContent] Has roots?', treeResponse && typeof treeResponse === 'object' && 'roots' in treeResponse);
        
        // Transform response to expected format
        const roots = transformTreeResponse(treeResponse);
        
        const options = convertSkillTreeToOptions(roots);
        setSkillOptions(options);
        console.log('[ResourceTabContent] Loaded skill tree:', {
          rootCount: roots.length,
          optionsCount: options.length,
          hasOptions: options.length > 0,
          firstRootName: roots.length > 0 ? roots[0].name : 'none',
          firstRootChildren: roots.length > 0 && roots[0].children ? roots[0].children.length : 0,
          sampleStructure: roots.length > 0 ? {
            id: roots[0].id,
            name: roots[0].name,
            hasChildren: !!roots[0].children && roots[0].children.length > 0,
            childrenCount: roots[0].children ? roots[0].children.length : 0
          } : null
        });
      } catch (err) {
        console.error('[ResourceTabContent] Error loading skill tree:', err);
        message.error('Failed to load skills tree');
        setSkillOptions([]);
      } finally {
        setLoadingSkills(false);
      }
    };
    loadSkillTree();
  }, [convertSkillTreeToOptions, transformTreeResponse]);

  // Initialize form with resource configurations when component mounts or validationRules changes
  React.useEffect(() => {
    const currentFormValidationRules = form.getFieldValue('validation_rules') || {};
    const resourceKeys = Object.keys(validationRules || {}).filter(key => key.match(/^resource\d+$/));
    
    console.log('[ResourceTabContent] Syncing validationRules with form:', {
      validationRules,
      resourceKeys,
      hasResources: resourceKeys.length > 0,
      currentFormKeys: Object.keys(currentFormValidationRules).filter(k => k.match(/^resource\d+$/)),
      resourceDetails: resourceKeys.map(key => ({
        key,
        config: validationRules?.[key]
      }))
    });
    
    // Always ensure validation_rules contains resource configurations from props
    // This ensures resources are available in form values for saving
    if (resourceKeys.length > 0) {
      const updates: Record<string, any> = {};
      resourceKeys.forEach(key => {
        if (validationRules && validationRules[key]) {
          updates[key] = validationRules[key];
        }
      });
      
      // Merge with existing form validation_rules, prioritizing prop values
      form.setFieldsValue({
        validation_rules: {
          ...currentFormValidationRules,
          ...updates
        }
      });
      console.log('[ResourceTabContent] Synced form validation_rules with resource configurations:', Object.keys(updates));
    } else if (Object.keys(currentFormValidationRules).some(k => k.match(/^resource\d+$/))) {
      // If form has resources but props don't, keep form values (user might have added new ones)
      console.log('[ResourceTabContent] Keeping existing form validation_rules (no resources in props)');
    }
  }, [validationRules, form]);

  const handleAddResource = () => {
    const existingNumbers = localResources.map(r => parseInt(r.key.match(/\d+$/)?.[0] || '0', 10));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const newKey = `resource${nextNumber}`;
    const newResource = {
      key: newKey,
      config: {
        resource_type: 'process',
        resource_skill: null,
        description: ''
      }
    };
    const updated = [...localResources, newResource];
    setLocalResources(updated);
    
    // Update form values - ensure ALL resources are included
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    
    // Add all resources to validation_rules
    updated.forEach(resource => {
      updatedValidationRules[resource.key] = resource.config;
    });
    
    console.log('[ResourceTabContent] Adding resource:', {
      newKey,
      allResourceKeys: updated.map(r => r.key),
      validationRulesKeys: Object.keys(updatedValidationRules).filter(k => k.match(/^resource\d+$/))
    });
    
    form.setFieldsValue({
      validation_rules: updatedValidationRules
    });
  };

  const handleDeleteResource = (resourceKey: string) => {
    const updated = localResources.filter(r => r.key !== resourceKey);
    setLocalResources(updated);
    
    // Update form values - rebuild validation_rules with remaining resources
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    
    // Remove the deleted resource
    delete updatedValidationRules[resourceKey];
    
    // Ensure remaining resources are in validation_rules
    updated.forEach(resource => {
      updatedValidationRules[resource.key] = resource.config;
    });
    
    console.log('[ResourceTabContent] Deleting resource:', {
      deletedKey: resourceKey,
      remainingResourceKeys: updated.map(r => r.key),
      validationRulesKeys: Object.keys(updatedValidationRules).filter(k => k.match(/^resource\d+$/))
    });
    
    form.setFieldsValue({ validation_rules: updatedValidationRules });
  };

  const handleResourceChange = (resourceKey: string, field: string, value: any) => {
    const updated = localResources.map(r => {
      if (r.key === resourceKey) {
        return {
          ...r,
          config: {
            ...r.config,
            [field]: value
          }
        };
      }
      return r;
    });
    setLocalResources(updated);
    
    // Update form values - ensure ALL resources are in validation_rules
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    
    // Update all resources in validation_rules
    updated.forEach(resource => {
      updatedValidationRules[resource.key] = resource.config;
    });
    
    console.log('[ResourceTabContent] Updating form with resource change:', {
      resourceKey,
      field,
      value,
      allResourceKeys: updated.map(r => r.key),
      validationRulesKeys: Object.keys(updatedValidationRules).filter(k => k.match(/^resource\d+$/))
    });
    
    form.setFieldsValue({
      validation_rules: updatedValidationRules
    });
    
    // Force form to recognize the change
    form.setFieldsValue({ validation_rules: updatedValidationRules });
  };

  // Convert selected skill paths (arrays) to skill IDs for storage
  // With multiple={true}, Cascader always passes string[][] (array of arrays)
  const handleSkillChange = (resourceKey: string, value: string[][] | null) => {
    let skillIds: string[] | null = null;
    
    if (value && Array.isArray(value) && value.length > 0) {
      // Extract the last element (skill ID) from each path
      skillIds = value.map(path => {
        if (Array.isArray(path) && path.length > 0) {
          return path[path.length - 1];
        }
        return '';
      }).filter(id => id !== '');
      
      // If only one skill selected, store as single string, otherwise as array
      if (skillIds.length === 0) {
        skillIds = null;
      } else if (skillIds.length === 1) {
        // Store as single string if only one skill
        handleResourceChange(resourceKey, 'resource_skill', skillIds[0]);
        return;
      }
    }
    
    handleResourceChange(resourceKey, 'resource_skill', skillIds);
  };

  // Convert stored skill ID(s) to cascader path format
  // For multiple selection, Cascader expects array of arrays (array of paths)
  // Since we're using multiple={true}, always return string[][]
  const getSkillPathForCascader = (skillId: string | string[] | null | undefined): string[][] | undefined => {
    if (!skillId || skillOptions.length === 0) return undefined;
    
    const findPath = (id: string, options: SkillCascaderOption[], currentPath: string[] = []): string[] | undefined => {
      for (const option of options) {
        const path = [...currentPath, option.value];
        if (option.value === id) {
          return path;
        }
        if (option.children && option.children.length > 0) {
          const found = findPath(id, option.children, path);
          if (found) return found;
        }
      }
      return undefined;
    };

    if (Array.isArray(skillId)) {
      // Multi-select: return array of paths (array of arrays)
      const paths = skillId.map(id => findPath(id, skillOptions)).filter((path): path is string[] => path !== undefined);
      return paths.length > 0 ? paths : undefined;
    } else {
      // Single select: wrap in array to match string[][] format for multiple mode
      const path = findPath(skillId, skillOptions);
      return path ? [path] : undefined;
    }
  };

  return (
    <>
      <Alert
        message="Resource Configurations"
        description="Define named resource configurations that can be referenced by change types. Each configuration specifies resource type, required skills, and description."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddResource}
        >
          Add Resource Configuration
        </Button>
      </div>

      {localResources.length === 0 ? (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
          No resource configurations defined. Click "Add Resource Configuration" to create one.
        </Text>
      ) : (
        <div>
          {localResources.map((resource) => (
            <Card
              key={resource.key}
              title={resource.key}
              extra={
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteResource(resource.key)}
                >
                  Delete
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              <Form.Item
                label="Resource Type"
                tooltip="Type of resource required"
              >
                <Select
                  value={resource.config?.resource_type || 'process'}
                  onChange={(value) => handleResourceChange(resource.key, 'resource_type', value)}
                  placeholder="Select resource type"
                  style={{ width: '100%' }}
                >
                  <Option value="process">Process Resource</Option>
                  <Option value="user">User</Option>
                  <Option value="system">System</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Required Skills"
                tooltip="Select required skills from the skills hierarchy. Navigate through categories to select any skill level (parent or child)."
              >
                <Cascader
                  style={{ width: '100%' }}
                  options={skillOptions}
                  value={getSkillPathForCascader(resource.config?.resource_skill)}
                  onChange={(value) => handleSkillChange(resource.key, value)}
                  placeholder="Click to browse skill hierarchy..."
                  multiple
                  maxTagCount="responsive"
                  showSearch={{
                    filter: (inputValue, path) => {
                      return path.some(option => 
                        option.label?.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    },
                    render: (inputValue, path, prefixCls) => {
                      return path.map(option => option.label).join(' / ');
                    }
                  }}
                  expandTrigger="hover"
                  changeOnSelect={true}
                  displayRender={(labels, selectedOptions) => {
                    if (!labels || labels.length === 0) return null;
                    return labels.map((label, index) => {
                      const option = selectedOptions?.[index];
                      const levelPrefix = option?.skill_level !== undefined ? `L${option.skill_level}:` : '';
                      return (
                        <span key={index}>
                          {index > 0 && ' > '}
                          {levelPrefix && <span style={{ fontSize: '11px', color: '#888', marginRight: 4 }}>{levelPrefix}</span>}
                          {label}
                        </span>
                      );
                    }).reduce((prev, curr) => [prev, curr] as any);
                  }}
                  loading={loadingSkills}
                />
              </Form.Item>

              <Form.Item
                label="Description"
                tooltip="Description of resource requirements"
              >
                <TextArea
                  rows={3}
                  value={resource.config?.description || ''}
                  onChange={(e) => handleResourceChange(resource.key, 'description', e.target.value)}
                  placeholder="Description of resource requirements"
                />
              </Form.Item>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// Validation Tab Content Component for managing named validation configurations
const ValidationTabContent: React.FC<ValidationTabContentProps> = ({ form, validationRules, propertyType }) => {
  // Get existing validation configurations from validation_rules
  const validationConfigs = React.useMemo(() => {
    console.log('[ValidationTabContent] Processing validationRules:', {
      validationRules,
      isObject: typeof validationRules === 'object',
      keys: validationRules ? Object.keys(validationRules) : []
    });
    
    if (!validationRules || typeof validationRules !== 'object') return [];
    const validations: Array<{ key: string; config: any }> = [];
    Object.keys(validationRules).forEach(key => {
      if (key.match(/^validation\d+$/)) {
        validations.push({ key, config: validationRules[key] });
      }
    });
    const sorted = validations.sort((a, b) => {
      const numA = parseInt(a.key.match(/\d+$/)?.[0] || '0', 10);
      const numB = parseInt(b.key.match(/\d+$/)?.[0] || '0', 10);
      return numA - numB;
    });
    console.log('[ValidationTabContent] Extracted validation configurations:', sorted);
    return sorted;
  }, [validationRules]);

  const [localValidations, setLocalValidations] = useState<Array<{ key: string; config: any }>>(validationConfigs);
  
  // Sync local state with prop changes
  React.useEffect(() => {
    console.log('[ValidationTabContent] Updating localValidations from validationConfigs:', validationConfigs);
    setLocalValidations(validationConfigs);
  }, [validationConfigs]);

  // Initialize form with validation configurations when component mounts or validationRules changes
  React.useEffect(() => {
    const currentFormValidationRules = form.getFieldValue('validation_rules') || {};
    const validationKeys = Object.keys(validationRules || {}).filter(key => key.match(/^validation\d+$/));
    
    console.log('[ValidationTabContent] Syncing validationRules with form:', {
      validationRules,
      validationKeys,
      hasValidations: validationKeys.length > 0,
      currentFormKeys: Object.keys(currentFormValidationRules).filter(k => k.match(/^validation\d+$/)),
      validationDetails: validationKeys.map(key => ({
        key,
        config: validationRules?.[key]
      }))
    });
    
    // Always ensure validation_rules contains validation configurations from props
    if (validationKeys.length > 0) {
      const updates: Record<string, any> = {};
      validationKeys.forEach(key => {
        if (validationRules && validationRules[key]) {
          updates[key] = validationRules[key];
        }
      });
      
      // Merge with existing form validation_rules, prioritizing prop values
      form.setFieldsValue({
        validation_rules: {
          ...currentFormValidationRules,
          ...updates
        }
      });
      console.log('[ValidationTabContent] Synced form validation_rules with validation configurations:', Object.keys(updates));
    } else if (Object.keys(currentFormValidationRules).some(k => k.match(/^validation\d+$/))) {
      // If form has validations but props don't, keep form values (user might have added new ones)
      console.log('[ValidationTabContent] Keeping existing form validation_rules (no validations in props)');
    }
  }, [validationRules, form]);

  const handleAddValidation = () => {
    const existingNumbers = localValidations.map(v => parseInt(v.key.match(/\d+$/)?.[0] || '0', 10));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const newKey = `validation${nextNumber}`;
    const newValidation = {
      key: newKey,
      config: {
        required: false,
        type_specific_validation: {}
      }
    };
    const updated = [...localValidations, newValidation];
    setLocalValidations(updated);
    
    // Update form values - ensure ALL validations are included
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    
    // Add all validations to validation_rules
    updated.forEach(validation => {
      updatedValidationRules[validation.key] = validation.config;
    });
    
    console.log('[ValidationTabContent] Adding validation:', {
      newKey,
      allValidationKeys: updated.map(v => v.key),
      validationRulesKeys: Object.keys(updatedValidationRules).filter(k => k.match(/^validation\d+$/))
    });
    
    form.setFieldsValue({
      validation_rules: updatedValidationRules
    });
  };

  const handleDeleteValidation = (validationKey: string) => {
    const updated = localValidations.filter(v => v.key !== validationKey);
    setLocalValidations(updated);
    
    // Update form values - rebuild validation_rules with remaining validations
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    
    // Remove the deleted validation
    delete updatedValidationRules[validationKey];
    
    // Ensure remaining validations are in validation_rules
    updated.forEach(validation => {
      updatedValidationRules[validation.key] = validation.config;
    });
    
    console.log('[ValidationTabContent] Deleting validation:', {
      deletedKey: validationKey,
      remainingValidationKeys: updated.map(v => v.key),
      validationRulesKeys: Object.keys(updatedValidationRules).filter(k => k.match(/^validation\d+$/))
    });
    
    form.setFieldsValue({ validation_rules: updatedValidationRules });
  };

  const handleValidationChange = (validationKey: string, field: string, value: any) => {
    const updated = localValidations.map(v => {
      if (v.key === validationKey) {
        const newConfig = { ...v.config };
        if (field === 'type_specific_validation') {
          // Handle nested type_specific_validation object
          newConfig.type_specific_validation = value;
        } else {
          newConfig[field] = value;
        }
        return {
          ...v,
          config: newConfig
        };
      }
      return v;
    });
    setLocalValidations(updated);
    
    // Update form values - ensure ALL validations are in validation_rules
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    
    // Update all validations in validation_rules
    updated.forEach(validation => {
      updatedValidationRules[validation.key] = validation.config;
    });
    
    console.log('[ValidationTabContent] Updating form with validation change:', {
      validationKey,
      field,
      value,
      allValidationKeys: updated.map(v => v.key),
      validationRulesKeys: Object.keys(updatedValidationRules).filter(k => k.match(/^validation\d+$/))
    });
    
    form.setFieldsValue({
      validation_rules: updatedValidationRules
    });
  };

  return (
    <>
      <Alert
        message="Named Validation Configurations"
        description="Create reusable validation configurations that can be referenced by change types. These are stored separately from property-level validation rules."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Button
        type="dashed"
        onClick={handleAddValidation}
        icon={<PlusOutlined />}
        style={{ width: '100%', marginBottom: 16 }}
      >
        Add Validation Configuration
      </Button>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {localValidations.map(validation => (
          <Card
            key={validation.key}
            title={
              <Space>
                <Text strong>{validation.key}</Text>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteValidation(validation.key)}
                >
                  Delete
                </Button>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <label style={{ display: 'block', marginBottom: 4 }}>Required</label>
                  <Switch
                    checked={validation.config?.required || false}
                    onChange={(checked) => handleValidationChange(validation.key, 'required', checked)}
                  />
                </Col>
              </Row>
              
              {propertyType === 'list' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Limit to List</label>
                    <Switch
                      checked={validation.config?.type_specific_validation?.limit_to_list || false}
                      onChange={(checked) => {
                        const currentTypeSpecific = validation.config?.type_specific_validation || {};
                        handleValidationChange(validation.key, 'type_specific_validation', {
                          ...currentTypeSpecific,
                          limit_to_list: checked,
                          ...(checked ? {} : { update_scope: currentTypeSpecific.update_scope || 'update_local' })
                        });
                      }}
                    />
                  </Col>
                  {!validation.config?.type_specific_validation?.limit_to_list && (
                    <Col span={12}>
                      <label style={{ display: 'block', marginBottom: 4 }}>Update Scope</label>
                      <Select
                        style={{ width: '100%' }}
                        value={validation.config?.type_specific_validation?.update_scope || 'update_local'}
                        onChange={(value) => {
                          const currentTypeSpecific = validation.config?.type_specific_validation || {};
                          handleValidationChange(validation.key, 'type_specific_validation', {
                            ...currentTypeSpecific,
                            update_scope: value
                          });
                        }}
                      >
                        <Option value="update_global">Update Global</Option>
                        <Option value="update_local">Update Local</Option>
                      </Select>
                    </Col>
                  )}
                </Row>
              )}
              
              {isNumericPropertyType(propertyType || '') && (
                <Row gutter={16}>
                  <Col span={12}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Minimum Value</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={validation.config?.type_specific_validation?.min}
                      onChange={(value) => {
                        const currentTypeSpecific = validation.config?.type_specific_validation || {};
                        handleValidationChange(validation.key, 'type_specific_validation', {
                          ...currentTypeSpecific,
                          min: value || undefined
                        });
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Maximum Value</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={validation.config?.type_specific_validation?.max}
                      onChange={(value) => {
                        const currentTypeSpecific = validation.config?.type_specific_validation || {};
                        handleValidationChange(validation.key, 'type_specific_validation', {
                          ...currentTypeSpecific,
                          max: value || undefined
                        });
                      }}
                    />
                  </Col>
                </Row>
              )}
              
              {(propertyType === 'string' || propertyType === 'url') && (
                <Row gutter={16}>
                  <Col span={12}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Minimum Length</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      value={validation.config?.type_specific_validation?.min_length}
                      onChange={(value) => {
                        const currentTypeSpecific = validation.config?.type_specific_validation || {};
                        handleValidationChange(validation.key, 'type_specific_validation', {
                          ...currentTypeSpecific,
                          min_length: value || undefined
                        });
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Maximum Length</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      value={validation.config?.type_specific_validation?.max_length}
                      onChange={(value) => {
                        const currentTypeSpecific = validation.config?.type_specific_validation || {};
                        handleValidationChange(validation.key, 'type_specific_validation', {
                          ...currentTypeSpecific,
                          max_length: value || undefined
                        });
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <label style={{ display: 'block', marginBottom: 4 }}>Pattern (Regex)</label>
                    <Input
                      placeholder="e.g., ^[a-zA-Z0-9]+$"
                      value={validation.config?.type_specific_validation?.pattern}
                      onChange={(e) => {
                        const currentTypeSpecific = validation.config?.type_specific_validation || {};
                        handleValidationChange(validation.key, 'type_specific_validation', {
                          ...currentTypeSpecific,
                          pattern: e.target.value || undefined
                        });
                      }}
                    />
                  </Col>
                </Row>
              )}
            </Space>
          </Card>
        ))}
        
        {localValidations.length === 0 && (
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
            No validation configurations defined. Click "Add Validation Configuration" to create one.
          </Text>
        )}
      </Space>
      
      {/* Hidden Form.Item to ensure validation_rules is tracked by the form */}
      <Form.Item name="validation_rules" hidden preserve>
        <Input type="hidden" />
      </Form.Item>
    </>
  );
};

// Reference Tab Content Component for managing named reference configurations
const ReferenceTabContent: React.FC<ReferenceTabContentProps> = ({ form, calculationDependencies }) => {
  // Get existing reference configurations from calculation_dependencies
  const referenceConfigs = React.useMemo(() => {
    if (!calculationDependencies || typeof calculationDependencies !== 'object') return [];
    const references: Array<{ key: string; config: any }> = [];
    Object.keys(calculationDependencies).forEach(key => {
      if (key.match(/^reference\d+$/)) {
        references.push({ key, config: calculationDependencies[key] });
      }
    });
    return references.sort((a, b) => {
      const numA = parseInt(a.key.match(/\d+$/)?.[0] || '0', 10);
      const numB = parseInt(b.key.match(/\d+$/)?.[0] || '0', 10);
      return numA - numB;
    });
  }, [calculationDependencies]);

  const [localReferences, setLocalReferences] = useState<Array<{ key: string; config: any }>>(referenceConfigs);

  React.useEffect(() => {
    setLocalReferences(referenceConfigs);
  }, [referenceConfigs]);

  const handleAddReference = () => {
    const existingNumbers = localReferences.map(r => parseInt(r.key.match(/\d+$/)?.[0] || '0', 10));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const newKey = `reference${nextNumber}`;
    const newReference: { key: string; config: ReferenceConfiguration } = {
      key: newKey,
      config: {
        artifact_type: undefined,
        artifact_item: 'operational',
        property_keys: [], // Initialize with empty array for multiple property keys
        property_key: undefined, // Legacy support
        filter: undefined,
        selection_method: 'process',
        source: 'same_artifact',
        reference_type: 'simple',
        description: undefined,
      }
    };
    const updated = [...localReferences, newReference];
    setLocalReferences(updated);
    
    const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
    const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
    updated.forEach(ref => {
      updatedCalculationDependencies[ref.key] = ref.config;
    });
    form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
  };

  const handleDeleteReference = (referenceKey: string) => {
    const updated = localReferences.filter(r => r.key !== referenceKey);
    setLocalReferences(updated);
    
    const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
    const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
    delete updatedCalculationDependencies[referenceKey];
    updated.forEach(ref => {
      updatedCalculationDependencies[ref.key] = ref.config;
    });
    form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
  };

  const handleReferenceChange = (referenceKey: string, field: string, value: any) => {
    const updated = localReferences.map(r => {
      if (r.key === referenceKey) {
        return {
          ...r,
          config: {
            ...r.config,
            [field]: value
          }
        };
      }
      return r;
    });
    setLocalReferences(updated);
    
    const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
    const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
    updated.forEach(ref => {
      updatedCalculationDependencies[ref.key] = ref.config;
    });
    form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
  };

  return (
    <>
      <Alert
        message="Named Reference Configurations"
        description="Define reusable reference configurations that can be referenced by change types. These specify how to retrieve values from other artifacts."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Button
        type="dashed"
        onClick={handleAddReference}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Reference Configuration
      </Button>

      {localReferences.length === 0 ? (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
          No named reference configurations defined.
        </Text>
      ) : (
        <div>
          {localReferences.map(reference => (
            <Card
              key={reference.key}
              size="small"
              style={{ marginBottom: 16 }}
              title={reference.key}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteReference(reference.key)}
                  title="Delete reference configuration"
                />
              }
            >
              <Alert
                message="Multiple Property Keys Supported"
                description="A reference configuration can contain multiple property keys. These property keys will be available for selection in calculation operands and other contexts."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item label="Property Keys">
                <div>
                  {(reference.config?.property_keys || (reference.config?.property_key ? [reference.config.property_key] : [])).map((propKey: string, index: number) => (
                    <Row key={index} gutter={8} style={{ marginBottom: 8 }}>
                      <Col span={20}>
                        <PropertyKeySelector
                          value={propKey}
                          onChange={(value) => {
                            const currentPropertyKeys = reference.config?.property_keys || (reference.config?.property_key ? [reference.config.property_key] : []);
                            const updatedPropertyKeys = [...currentPropertyKeys];
                            updatedPropertyKeys[index] = value;
                            handleReferenceChange(reference.key, 'property_keys', updatedPropertyKeys);
                            // Clear legacy property_key if using new format
                            if (reference.config?.property_key && updatedPropertyKeys.length > 0) {
                              handleReferenceChange(reference.key, 'property_key', undefined);
                            }
                          }}
                          placeholder="Select property key"
                        />
                      </Col>
                      <Col span={4}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            const currentPropertyKeys = reference.config?.property_keys || (reference.config?.property_key ? [reference.config.property_key] : []);
                            const updatedPropertyKeys = currentPropertyKeys.filter((_: string, i: number) => i !== index);
                            handleReferenceChange(reference.key, 'property_keys', updatedPropertyKeys.length > 0 ? updatedPropertyKeys : undefined);
                          }}
                          title="Remove property key"
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => {
                      const currentPropertyKeys = reference.config?.property_keys || (reference.config?.property_key ? [reference.config.property_key] : []);
                      handleReferenceChange(reference.key, 'property_keys', [...currentPropertyKeys, '']);
                      // Clear legacy property_key if using new format
                      if (reference.config?.property_key) {
                        handleReferenceChange(reference.key, 'property_key', undefined);
                      }
                    }}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add Property Key
                  </Button>
                </div>
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Source">
                    <Select
                      value={reference.config?.source}
                      onChange={(value) => handleReferenceChange(reference.key, 'source', value)}
                      placeholder="Select source"
                    >
                      {SOURCE_TYPES.map(type => (
                        <Option key={type.value} value={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Artifact Item">
                    <Select
                      value={reference.config?.artifact_item}
                      onChange={(value) => handleReferenceChange(reference.key, 'artifact_item', value)}
                      placeholder="Select artifact item"
                    >
                      <Option value="operational">Operational</Option>
                      <Option value="improvement">Improvement</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Selection Method">
                    <Select
                      value={reference.config?.selection_method}
                      onChange={(value) => handleReferenceChange(reference.key, 'selection_method', value)}
                      placeholder="Select method"
                    >
                      <Option value="process">Process</Option>
                      <Option value="user">User</Option>
                      <Option value="system">System</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              {reference.config?.source && reference.config.source !== 'same_artifact' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Artifact Type">
                      <ArtifactTypeSelector
                        value={reference.config?.artifact_type}
                        onChange={(value) => handleReferenceChange(reference.key, 'artifact_type', value)}
                        placeholder="Select artifact type"
                        allowClear
                        showSearch
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Filter">
                      <Input
                        value={reference.config?.filter}
                        onChange={(e) => handleReferenceChange(reference.key, 'filter', e.target.value)}
                        placeholder="Filter expression"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <Form.Item label="Description">
                <TextArea
                  rows={2}
                  value={reference.config?.description}
                  onChange={(e) => handleReferenceChange(reference.key, 'description', e.target.value)}
                  placeholder="Description of this reference configuration"
                />
              </Form.Item>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// Calculation Tab Content Component for managing named calculation configurations
const CalculationTabContent: React.FC<CalculationTabContentProps> = ({ form, propertyType, calculationDependencies }) => {
  // Get existing calculation configurations from calculation_dependencies
  const calculationConfigs = React.useMemo(() => {
    if (!calculationDependencies || typeof calculationDependencies !== 'object') return [];
    const calculations: Array<{ key: string; config: any }> = [];
    
    // Check if calculation_dependencies is an array (old format) - convert to named configuration
    if (Array.isArray(calculationDependencies)) {
      // Convert old array format to named calculation configuration
      // For divide operations, assume first is numerator, second is denominator
      // For other operations, we'll need to infer from context or use a default
      const existingNumbers: number[] = [];
      const nextNumber = 1;
      const newKey = `calculation${nextNumber}`;
      
      // Try to infer calculation type from property description or default to 'divide'
      const calcType = 'divide'; // Default, could be enhanced to detect from description
      
      // Create a reference configuration for the property_keys
      const existingRefNumbers: number[] = [];
      const nextRefNumber = 1;
      const newRefKey = `reference${nextRefNumber}`;
      
      // Convert to source_properties format using reference configuration
      const sourceProperties = calculationDependencies.map((propKey: string, index: number) => {
        if (calcType === 'divide') {
          return {
            operator: index === 0 ? 'numerator' : 'denominator',
            reference: newRefKey,
            property_index: index
          };
        } else {
          return {
            operator: `operand${index + 1}`,
            reference: newRefKey,
            property_index: index
          };
        }
      });
      
      calculations.push({
        key: newKey,
        config: {
          calculation_type: calcType,
          source_properties: sourceProperties
        }
      });
      
      // Update form with converted calculation_dependencies
      const currentFormCalcDeps = form.getFieldValue('calculation_dependencies') || {};
      const updatedCalculationDependencies: Record<string, any> = { ...currentFormCalcDeps };
      
      // Add reference configuration with property_keys
      updatedCalculationDependencies[newRefKey] = {
        artifact_item: 'operational',
        selection_method: 'process',
        source: 'same_artifact',
        reference_type: 'simple',
        property_keys: calculationDependencies
      };
      
      // Add calculation configuration
      updatedCalculationDependencies[newKey] = calculations[0].config;
      
      // Remove array indices if they exist
      calculationDependencies.forEach((_: any, index: number) => {
        delete updatedCalculationDependencies[index.toString()];
      });
      form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
    } else {
      // Check if this is an object with numeric keys (old array format stored as object)
      const numericKeys = Object.keys(calculationDependencies).filter(key => key.match(/^\d+$/));
      const hasNumericKeys = numericKeys.length > 0;
      
      if (hasNumericKeys) {
        // Convert object with numeric keys to named calculation configuration
        const propKeys = numericKeys
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
          .map(key => calculationDependencies[key])
          .filter((val): val is string => typeof val === 'string');
        
        if (propKeys.length > 0) {
          const existingCalcNumbers = Object.keys(calculationDependencies)
            .filter(key => key.match(/^calculation\d+$/))
            .map(key => parseInt(key.match(/\d+$/)?.[0] || '0', 10));
          const nextCalcNumber = existingCalcNumbers.length > 0 ? Math.max(...existingCalcNumbers) + 1 : 1;
          const newCalcKey = `calculation${nextCalcNumber}`;
          
          // Create a reference configuration for the property_keys
          const existingRefNumbers = Object.keys(calculationDependencies)
            .filter(key => key.match(/^reference\d+$/))
            .map(key => parseInt(key.match(/\d+$/)?.[0] || '0', 10));
          const nextRefNumber = existingRefNumbers.length > 0 ? Math.max(...existingRefNumbers) + 1 : 1;
          const newRefKey = `reference${nextRefNumber}`;
          
          // Try to infer calculation type from property description or default to 'divide'
          const calcType = 'divide'; // Default, could be enhanced to detect from description
          
          // Convert to source_properties format using reference configuration
          const sourceProperties = propKeys.map((propKey: string, index: number) => {
            if (calcType === 'divide') {
              return {
                operator: index === 0 ? 'numerator' : 'denominator',
                reference: newRefKey,
                property_index: index
              };
            } else {
              return {
                operator: `operand${index + 1}`,
                reference: newRefKey,
                property_index: index
              };
            }
          });
          
          calculations.push({
            key: newCalcKey,
            config: {
              calculation_type: calcType,
              source_properties: sourceProperties
            }
          });
          
          // Update form with converted calculation_dependencies
          const currentFormCalcDeps = form.getFieldValue('calculation_dependencies') || {};
          const updatedCalculationDependencies: Record<string, any> = { ...currentFormCalcDeps };
          
          // Add reference configuration with property_keys
          updatedCalculationDependencies[newRefKey] = {
            artifact_item: 'operational',
            selection_method: 'process',
            source: 'same_artifact',
            reference_type: 'simple',
            property_keys: propKeys
          };
          
          // Add calculation configuration
          updatedCalculationDependencies[newCalcKey] = calculations[0].config;
          
          // Remove numeric keys
          numericKeys.forEach(key => {
            delete updatedCalculationDependencies[key];
          });
          form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
        }
      }
      
      // New format: extract named calculation configurations
      Object.keys(calculationDependencies).forEach(key => {
        // Skip array indices (old format remnants)
        if (key.match(/^\d+$/)) {
          return;
        }
        if (key.match(/^calculation\d+$/)) {
          const config = { ...calculationDependencies[key] }; // Create a copy to avoid mutating original
          // Ensure source_properties is an array
          if (config && typeof config === 'object') {
            if (config.source_properties && !Array.isArray(config.source_properties)) {
              // Convert object with length property or any non-array to empty array
              config.source_properties = [];
            } else if (!config.source_properties && ['add', 'subtract', 'multiply', 'divide'].includes(config.calculation_type)) {
              // Initialize source_properties for math operations if missing
              config.source_properties = [];
            }
          }
          calculations.push({ key, config });
        }
      });
    }
    
    // For Boolean properties: check if boolean_conditions exist and create/update a calculation config for them
    if (propertyType === 'boolean' && calculationDependencies && typeof calculationDependencies === 'object' && !Array.isArray(calculationDependencies) && calculationDependencies.boolean_conditions) {
      // Find existing boolean calculation or create one
      let booleanCalc = calculations.find(c => c.config?.calculation_type === 'boolean');
      if (!booleanCalc) {
        // Create a new calculation config for boolean conditions
        const existingNumbers = calculations.map(c => parseInt(c.key.match(/\d+$/)?.[0] || '0', 10));
        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
        const newKey = `calculation${nextNumber}`;
        booleanCalc = {
          key: newKey,
          config: {
            calculation_type: 'boolean',
            boolean_conditions: calculationDependencies.boolean_conditions
          }
        };
        calculations.push(booleanCalc);
      } else {
        // Merge boolean_conditions from calculationDependencies if they exist
        if (calculationDependencies.boolean_conditions) {
          booleanCalc.config.boolean_conditions = {
            ...booleanCalc.config.boolean_conditions,
            ...calculationDependencies.boolean_conditions
          };
        }
      }
    }
    
    return calculations.sort((a, b) => {
      const numA = parseInt(a.key.match(/\d+$/)?.[0] || '0', 10);
      const numB = parseInt(b.key.match(/\d+$/)?.[0] || '0', 10);
      return numA - numB;
    });
  }, [calculationDependencies, propertyType, form]);

  const [localCalculations, setLocalCalculations] = useState<Array<{ key: string; config: any }>>(calculationConfigs);

  React.useEffect(() => {
    setLocalCalculations(calculationConfigs);
  }, [calculationConfigs]);

  // Sync with form
  React.useEffect(() => {
    const currentFormCalcDeps = form.getFieldValue('calculation_dependencies') || {};
    const calcKeys = Object.keys(calculationDependencies || {}).filter(key => key.match(/^calculation\d+$/));
    
    if (calcKeys.length > 0) {
      const updates: Record<string, any> = {};
      calcKeys.forEach(key => {
        if (calculationDependencies && calculationDependencies[key]) {
          updates[key] = calculationDependencies[key];
        }
      });
      
      form.setFieldsValue({
        calculation_dependencies: {
          ...currentFormCalcDeps,
          ...updates
        }
      });
    }
  }, [calculationDependencies, form]);

  const handleAddCalculation = () => {
    const existingNumbers = localCalculations.map(c => parseInt(c.key.match(/\d+$/)?.[0] || '0', 10));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const newKey = `calculation${nextNumber}`;
    const newCalculation = {
      key: newKey,
      config: {
        calculation_type: 'function',
        function_name: '',
        function_parameters: {},
        source_properties: []
      }
    };
    const updated = [...localCalculations, newCalculation];
    setLocalCalculations(updated);
    
    const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
    const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
    updated.forEach(calc => {
      updatedCalculationDependencies[calc.key] = calc.config;
    });
    form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
  };

  const handleDeleteCalculation = (calculationKey: string) => {
    const updated = localCalculations.filter(c => c.key !== calculationKey);
    setLocalCalculations(updated);
    
    const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
    const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
    delete updatedCalculationDependencies[calculationKey];
    updated.forEach(calc => {
      updatedCalculationDependencies[calc.key] = calc.config;
    });
    form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
  };

  const handleCalculationChange = (calculationKey: string, field: string, value: any) => {
    const updated = localCalculations.map(c => {
      if (c.key === calculationKey) {
        let newConfig = { ...c.config, [field]: value };
        // Initialize sum_config when calculation_type changes to 'sum'
        if (field === 'calculation_type' && value === 'sum' && !newConfig.sum_config) {
          newConfig.sum_config = {
            property_key: undefined,
            artifact_filter: 'all',
            date_range_filter: undefined,
            property_filters: []
          };
        } else if (field === 'calculation_type' && value !== 'sum') {
          // Clear sum_config if calculation_type is no longer 'sum'
          delete newConfig.sum_config;
        }
        // Initialize boolean_conditions when calculation_type changes to 'boolean'
        if (field === 'calculation_type' && value === 'boolean' && !newConfig.boolean_conditions) {
          newConfig.boolean_conditions = {
            true_condition: {},
            false_condition: {}
          };
        } else if (field === 'calculation_type' && value !== 'boolean') {
          // Clear boolean_conditions if calculation_type is no longer 'boolean'
          delete newConfig.boolean_conditions;
        }
        // Initialize source_properties for math operations (add, subtract, multiply, divide)
        if (field === 'calculation_type' && ['add', 'subtract', 'multiply', 'divide'].includes(value)) {
          if (!Array.isArray(newConfig.source_properties)) {
            newConfig.source_properties = [];
          }
        }
        return {
          ...c,
          config: newConfig
        };
      }
      return c;
    });
    setLocalCalculations(updated);
    
    const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
    const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
    updated.forEach(calc => {
      updatedCalculationDependencies[calc.key] = calc.config;
      // For boolean calculations, also store in boolean_conditions for backward compatibility
      if (calc.config?.calculation_type === 'boolean' && calc.config?.boolean_conditions) {
        updatedCalculationDependencies.boolean_conditions = calc.config.boolean_conditions;
      }
    });
    form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
  };

  // Helper to get the boolean calculation configuration
  const getBooleanCalculation = () => {
    return localCalculations.find(c => c.config?.calculation_type === 'boolean');
  };


  // Auto-create boolean calculation for boolean properties if it doesn't exist
  // This runs once when propertyType is boolean and no boolean calculation exists
  const hasAutoCreatedBoolean = React.useRef(false);
  React.useEffect(() => {
    if (propertyType === 'boolean' && !hasAutoCreatedBoolean.current) {
      const booleanCalc = getBooleanCalculation();
      if (!booleanCalc) {
        hasAutoCreatedBoolean.current = true;
        // Check if boolean_conditions exist in calculationDependencies (backward compatibility)
        const hasBooleanConditions = calculationDependencies && typeof calculationDependencies === 'object' && calculationDependencies.boolean_conditions;
        
        // Auto-create boolean calculation configuration
        const existingNumbers = localCalculations.map(c => parseInt(c.key.match(/\d+$/)?.[0] || '0', 10));
        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
        const newKey = `calculation${nextNumber}`;
        const newBooleanCalc = {
          key: newKey,
          config: {
            calculation_type: 'boolean',
            boolean_conditions: hasBooleanConditions || {
              true_condition: {},
              false_condition: {}
            }
          }
        };
        const updated = [...localCalculations, newBooleanCalc];
        setLocalCalculations(updated);
        
        const currentCalculationDependencies = form.getFieldValue('calculation_dependencies') || {};
        const updatedCalculationDependencies: Record<string, any> = { ...currentCalculationDependencies };
        updated.forEach(calc => {
          updatedCalculationDependencies[calc.key] = calc.config;
        });
        // Also store in boolean_conditions for backward compatibility
        updatedCalculationDependencies.boolean_conditions = newBooleanCalc.config.boolean_conditions;
        form.setFieldsValue({ calculation_dependencies: updatedCalculationDependencies });
      }
    }
    // Reset when propertyType changes away from boolean
    if (propertyType !== 'boolean') {
      hasAutoCreatedBoolean.current = false;
    }
  }, [propertyType]);

  return (
    <>
      <Alert
        message="Named Calculation Configurations"
        description="Define reusable calculation configurations that can be referenced by change types. Each configuration specifies calculation type, function, and source properties. For Boolean properties, use calculation type 'Boolean' to configure True/False conditions."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Button
        type="dashed"
        onClick={handleAddCalculation}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Calculation Configuration
      </Button>

      {localCalculations.length === 0 ? (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
          No calculation configurations defined.
        </Text>
      ) : (
        <div>
          {localCalculations.map(calculation => (
            <Card
              key={calculation.key}
              size="small"
              style={{ marginBottom: 16 }}
              title={calculation.key}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteCalculation(calculation.key)}
                  title="Delete calculation configuration"
                />
              }
            >
              <Form.Item label="Calculation Type">
                <Select
                  value={calculation.config?.calculation_type || 'function'}
                  onChange={(value) => {
                    handleCalculationChange(calculation.key, 'calculation_type', value);
                    // Initialize sum_config when sum is selected
                    if (value === 'sum') {
                      const currentCalculationConfig = calculation.config?.calculation_config || {};
                      if (!currentCalculationConfig.sum_config) {
                        handleCalculationChange(calculation.key, 'calculation_config', {
                          ...currentCalculationConfig,
                          sum_config: {
                            property_key: '',
                            artifact_filter: 'all',
                            date_range_filter: undefined,
                            property_filters: []
                          }
                        });
                      }
                    }
                    // Initialize source_properties for avg, min, max if not already set
                    if (['avg', 'min', 'max'].includes(value) && !calculation.config?.source_properties) {
                      handleCalculationChange(calculation.key, 'source_properties', []);
                    }
                    // Initialize boolean_conditions when boolean is selected
                    if (value === 'boolean' && !calculation.config?.boolean_conditions) {
                      handleCalculationChange(calculation.key, 'boolean_conditions', {
                        true_condition: {},
                        false_condition: {}
                      });
                    }
                  }}
                  placeholder="Select calculation type"
                  style={{ width: '100%' }}
                >
                  {CALCULATION_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Boolean calculation configuration */}
              {calculation.config?.calculation_type === 'boolean' && (
                <>
                  <Divider orientation="left">Boolean Conditions</Divider>
                  <Alert
                    message="Boolean Property Conditions"
                    description="Define conditions that determine when this Boolean property should be true or false. At least one condition (True or False) must be defined."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  {(() => {
                    const booleanConditions = calculation.config?.boolean_conditions || {};
                    const trueCondition = booleanConditions.true_condition || {};
                    const falseCondition = booleanConditions.false_condition || {};

                    const renderConditionSection = (
                      conditionType: 'true_condition' | 'false_condition',
                      condition: any,
                      title: string
                    ) => (
                      <Card
                        key={conditionType}
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={title}
                      >
                        <Form.Item label="User Input">
                          <Checkbox
                            checked={condition.user_input || false}
                            onChange={(e) => {
                              const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                              const currentCondition = currentBooleanConditions[conditionType] || {};
                              const updatedCondition = {
                                ...currentCondition,
                                user_input: e.target.checked
                              };
                              const updatedBooleanConditions = {
                                ...currentBooleanConditions,
                                [conditionType]: updatedCondition
                              };
                              handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                            }}
                          >
                            Property is {conditionType === 'true_condition' ? 'true' : 'false'} when user sets it
                          </Checkbox>
                        </Form.Item>

                        <Divider orientation="left">In State Condition</Divider>
                        <Form.Item label="Artifact Item">
                          <Radio.Group
                            value={condition.state_condition?.artifact_item || 'current'}
                            onChange={(e) => {
                              const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                              const currentCondition = currentBooleanConditions[conditionType] || {};
                              const currentStateCondition = currentCondition.state_condition || {};
                              const updatedStateCondition = {
                                ...currentStateCondition,
                                artifact_item: e.target.value
                              };
                              const updatedCondition = {
                                ...currentCondition,
                                state_condition: updatedStateCondition
                              };
                              const updatedBooleanConditions = {
                                ...currentBooleanConditions,
                                [conditionType]: updatedCondition
                              };
                              handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                            }}
                          >
                            <Radio value="current">Current artifact_item</Radio>
                            <Radio value="reference">Reference artifact_item</Radio>
                          </Radio.Group>
                        </Form.Item>
                        {condition.state_condition?.artifact_item === 'reference' && (
                          <Form.Item label="Reference Artifact Item">
                            <PropertyKeySelector
                              value={condition.state_condition?.reference_artifact_item || ''}
                              onChange={(value) => {
                                const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                const currentCondition = currentBooleanConditions[conditionType] || {};
                                const currentStateCondition = currentCondition.state_condition || {};
                                const updatedStateCondition = {
                                  ...currentStateCondition,
                                  reference_artifact_item: value
                                };
                                const updatedCondition = {
                                  ...currentCondition,
                                  state_condition: updatedStateCondition
                                };
                                const updatedBooleanConditions = {
                                  ...currentBooleanConditions,
                                  [conditionType]: updatedCondition
                                };
                                handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                              }}
                              placeholder="Select reference artifact_item"
                            />
                          </Form.Item>
                        )}
                        <Form.Item label="State">
                          <Input
                            value={condition.state_condition?.state || 'defined'}
                            onChange={(e) => {
                              const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                              const currentCondition = currentBooleanConditions[conditionType] || {};
                              const currentStateCondition = currentCondition.state_condition || {};
                              const updatedStateCondition = {
                                ...currentStateCondition,
                                state: e.target.value
                              };
                              const updatedCondition = {
                                ...currentCondition,
                                state_condition: updatedStateCondition
                              };
                              const updatedBooleanConditions = {
                                ...currentBooleanConditions,
                                [conditionType]: updatedCondition
                              };
                              handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                            }}
                            placeholder="e.g., defined"
                          />
                        </Form.Item>

                        <Divider orientation="left">Property Value Conditions</Divider>
                        {(Array.isArray(condition.property_value) ? condition.property_value : []).map((propValue: any, index: number) => (
                          <Card key={index} size="small" style={{ marginBottom: 8 }}>
                            <Row gutter={16}>
                              <Col span={24}>
                                <Form.Item label="Artifact Item">
                                  <Radio.Group
                                    value={propValue.artifact_item || 'current'}
                                    onChange={(e) => {
                                      const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                      const currentCondition = currentBooleanConditions[conditionType] || {};
                                      const currentPropertyValues = currentCondition.property_value || [];
                                      const updatedPropertyValues = [...currentPropertyValues];
                                      if (!updatedPropertyValues[index]) {
                                        updatedPropertyValues[index] = {};
                                      }
                                      updatedPropertyValues[index] = {
                                        ...updatedPropertyValues[index],
                                        artifact_item: e.target.value
                                      };
                                      const updatedCondition = {
                                        ...currentCondition,
                                        property_value: updatedPropertyValues
                                      };
                                      const updatedBooleanConditions = {
                                        ...currentBooleanConditions,
                                        [conditionType]: updatedCondition
                                      };
                                      handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                                    }}
                                  >
                                    <Radio value="current">Current artifact_item</Radio>
                                    <Radio value="reference">Reference artifact_item</Radio>
                                  </Radio.Group>
                                </Form.Item>
                              </Col>
                              {propValue.artifact_item === 'reference' && (
                                <Col span={24}>
                                  <Form.Item label="Reference Artifact Item">
                                    <PropertyKeySelector
                                      value={propValue.reference_artifact_item || ''}
                                      onChange={(value) => {
                                        const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                        const currentCondition = currentBooleanConditions[conditionType] || {};
                                        const currentPropertyValues = currentCondition.property_value || [];
                                        const updatedPropertyValues = [...currentPropertyValues];
                                        if (!updatedPropertyValues[index]) {
                                          updatedPropertyValues[index] = {};
                                        }
                                        updatedPropertyValues[index] = {
                                          ...updatedPropertyValues[index],
                                          reference_artifact_item: value
                                        };
                                        const updatedCondition = {
                                          ...currentCondition,
                                          property_value: updatedPropertyValues
                                        };
                                        const updatedBooleanConditions = {
                                          ...currentBooleanConditions,
                                          [conditionType]: updatedCondition
                                        };
                                        handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                                      }}
                                      placeholder="Select reference artifact_item"
                                    />
                                  </Form.Item>
                                </Col>
                              )}
                              <Col span={8}>
                                <Form.Item label="Property Key">
                                  <PropertyKeySelector
                                    value={propValue.property_key || ''}
                                    onChange={(value) => {
                                      const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                      const currentCondition = currentBooleanConditions[conditionType] || {};
                                      const currentPropertyValues = currentCondition.property_value || [];
                                      const updatedPropertyValues = [...currentPropertyValues];
                                      if (!updatedPropertyValues[index]) {
                                        updatedPropertyValues[index] = {};
                                      }
                                      updatedPropertyValues[index] = {
                                        ...updatedPropertyValues[index],
                                        property_key: value
                                      };
                                      const updatedCondition = {
                                        ...currentCondition,
                                        property_value: updatedPropertyValues
                                      };
                                      const updatedBooleanConditions = {
                                        ...currentBooleanConditions,
                                        [conditionType]: updatedCondition
                                      };
                                      handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                                    }}
                                    placeholder="Property key"
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item label="Operator">
                                  <Select
                                    value={propValue.operator || '='}
                                    onChange={(value) => {
                                      const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                      const currentCondition = currentBooleanConditions[conditionType] || {};
                                      const currentPropertyValues = currentCondition.property_value || [];
                                      const updatedPropertyValues = [...currentPropertyValues];
                                      if (!updatedPropertyValues[index]) {
                                        updatedPropertyValues[index] = {};
                                      }
                                      updatedPropertyValues[index] = {
                                        ...updatedPropertyValues[index],
                                        operator: value
                                      };
                                      const updatedCondition = {
                                        ...currentCondition,
                                        property_value: updatedPropertyValues
                                      };
                                      const updatedBooleanConditions = {
                                        ...currentBooleanConditions,
                                        [conditionType]: updatedCondition
                                      };
                                      handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                                    }}
                                    style={{ width: '100%' }}
                                  >
                                    <Option value="=">=</Option>
                                    <Option value="!=">!=</Option>
                                    <Option value=">">&gt;</Option>
                                    <Option value="<">&lt;</Option>
                                    <Option value=">=">&gt;=</Option>
                                    <Option value="<=">&lt;=</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={10}>
                                <Form.Item label="Value">
                                  <Input
                                    value={propValue.value || ''}
                                    onChange={(e) => {
                                      const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                      const currentCondition = currentBooleanConditions[conditionType] || {};
                                      const currentPropertyValues = currentCondition.property_value || [];
                                      const updatedPropertyValues = [...currentPropertyValues];
                                      if (!updatedPropertyValues[index]) {
                                        updatedPropertyValues[index] = {};
                                      }
                                      updatedPropertyValues[index] = {
                                        ...updatedPropertyValues[index],
                                        value: e.target.value
                                      };
                                      const updatedCondition = {
                                        ...currentCondition,
                                        property_value: updatedPropertyValues
                                      };
                                      const updatedBooleanConditions = {
                                        ...currentBooleanConditions,
                                        [conditionType]: updatedCondition
                                      };
                                      handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                                    }}
                                    placeholder="Value"
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={2}>
                                <Form.Item label=" ">
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                                      const currentCondition = currentBooleanConditions[conditionType] || {};
                                      const currentPropertyValues = currentCondition.property_value || [];
                                      const updatedPropertyValues = currentPropertyValues.filter((_: any, i: number) => i !== index);
                                      const updatedCondition = {
                                        ...currentCondition,
                                        property_value: updatedPropertyValues
                                      };
                                      const updatedBooleanConditions = {
                                        ...currentBooleanConditions,
                                        [conditionType]: updatedCondition
                                      };
                                      handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                                    }}
                                    title="Remove property value condition"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Card>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => {
                            const currentBooleanConditions = calculation.config?.boolean_conditions || {};
                            const currentCondition = currentBooleanConditions[conditionType] || {};
                            const currentPropertyValues = currentCondition.property_value || [];
                            const updatedPropertyValues = [
                              ...currentPropertyValues,
                              { artifact_item: 'current', property_key: '', operator: '=', value: '' }
                            ];
                            const updatedCondition = {
                              ...currentCondition,
                              property_value: updatedPropertyValues
                            };
                            const updatedBooleanConditions = {
                              ...currentBooleanConditions,
                              [conditionType]: updatedCondition
                            };
                            handleCalculationChange(calculation.key, 'boolean_conditions', updatedBooleanConditions);
                          }}
                          icon={<PlusOutlined />}
                          block
                          style={{ marginTop: 8 }}
                        >
                          Add Property Value Condition
                        </Button>
                      </Card>
                    );

                    return (
                      <>
                        {renderConditionSection('true_condition', trueCondition, 'True Condition')}
                        {renderConditionSection('false_condition', falseCondition, 'False Condition')}
                      </>
                    );
                  })()}
                </>
              )}

              {calculation.config?.calculation_type === 'function' && (
                <>
                  <Form.Item label="Function Name">
                    <Select
                      value={calculation.config?.function_name}
                      onChange={(value) => handleCalculationChange(calculation.key, 'function_name', value)}
                      placeholder="Select function"
                      style={{ width: '100%' }}
                    >
                      {FUNCTION_TYPES
                        .filter(func => {
                          // If function has propertyTypes restriction, only show if current propertyType matches
                          if ((func as any).propertyTypes) {
                            return (func as any).propertyTypes.includes(propertyType);
                          }
                          // Functions without propertyTypes restriction are shown for all types
                          return true;
                        })
                        .map(func => (
                          <Option key={func.value} value={func.value}>
                            {func.label}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                  
                  {/* Function Parameters - get_krysalis_user */}
                  {calculation.config?.function_name === 'get_krysalis_user' ? (
                    <>
                      <Divider orientation="left">Function Parameters</Divider>
                      <Form.Item label="Input Type">
                        <Select
                          value={calculation.config?.function_parameters?.input_type}
                          onChange={(value) => {
                            const currentParams = calculation.config?.function_parameters || {};
                            handleCalculationChange(calculation.key, 'function_parameters', {
                              ...currentParams,
                              input_type: value
                            });
                          }}
                          placeholder="Select input type"
                          style={{ width: '100%' }}
                        >
                          {KRYSA_USER_INPUT_TYPES.map(type => (
                            <Option key={type.value} value={type.value}>
                              {type.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item label="Output Field">
                        <Select
                          value={calculation.config?.function_parameters?.output_field}
                          onChange={(value) => {
                            const currentParams = calculation.config?.function_parameters || {};
                            handleCalculationChange(calculation.key, 'function_parameters', {
                              ...currentParams,
                              output_field: value
                            });
                          }}
                          placeholder="Select output field"
                          style={{ width: '100%' }}
                        >
                          {KRYSA_USER_OUTPUT_FIELDS.map(field => (
                            <Option key={field.value} value={field.value}>
                              {field.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </>
                  ) : calculation.config?.function_name === 'transaction_status' ? (
                    // transaction_status has no parameters - system provides artifact_item_ref
                    <Alert
                      message="No Parameters Required"
                      description="This function requires no parameters. The system will automatically provide the artifact_item_ref."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : calculation.config?.function_name === 'Assessment' ? (
                    // Assessment parameters are configured in prime artifact properties, not global properties
                    <Alert
                      message="Assessment Function Parameters"
                      description="Assessment function parameters (assessment_element) are configured in prime artifact properties, not in global properties. The function will use artifact_item_ref (provided by the system) and derive the artifact_tree to run the assessment, producing an integer score between 1 and 10."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : calculation.config?.function_name === 'generate_date_range' ? (
                    // generate_date_range - for date_range properties
                    // Key forces remount when switching calculations to reset local state
                    <GenerateDateRangeConfig
                      key={`${calculation.key}-generate-date-range`}
                      calculation={calculation}
                      handleCalculationChange={handleCalculationChange}
                    />
                  ) : calculation.config?.function_name === 'generate_improvement_effective_date_range' ? (
                    // generate_improvement_effective_date_range - for improvement date_range properties
                    // Key forces remount when switching calculations to reset local state
                    <GenerateImprovementEffectiveDateRangeConfig
                      key={`${calculation.key}-generate-improvement-effective-date-range`}
                      calculation={calculation}
                      handleCalculationChange={handleCalculationChange}
                    />
                  ) : calculation.config?.function_name === 'transition_date' ? (
                    // transition_date - for date properties, no parameters needed
                    <Alert
                      message="No Parameters Required"
                      description="This function automatically records the current timestamp (UTC) when an artifact item transitions to a selected state. The value will be stored in ISO 8601 format."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : (
                    <Form.Item label="Function Parameters (JSON)">
                      <TextArea
                        rows={3}
                        value={JSON.stringify(calculation.config?.function_parameters || {}, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            handleCalculationChange(calculation.key, 'function_parameters', parsed);
                          } catch (err) {
                            // Invalid JSON, ignore for now
                          }
                        }}
                        placeholder='{"key": "value"}'
                      />
                    </Form.Item>
                  )}
                </>
              )}

              {/* Math operations (add, subtract, multiply, divide) */}
              {['add', 'subtract', 'multiply', 'divide'].includes(calculation.config?.calculation_type || '') && (
                <>
                  <Divider orientation="left">Operands</Divider>
                  <Alert
                    message="Reference Configuration Required"
                    description="Operands must reference a named reference configuration. Each reference configuration can contain multiple property keys. Select a reference configuration and then choose which property key from that configuration to use."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <div>
                    {(Array.isArray(calculation.config?.source_properties) ? calculation.config.source_properties : []).map((sourceProp: any, index: number) => {
                      const calcType = calculation.config?.calculation_type;
                      
                      // Get operator options based on calculation type
                      const getOperatorOptions = () => {
                        if (calcType === 'divide') {
                          return [
                            { value: 'numerator', label: 'Numerator (Dividend)' },
                            { value: 'denominator', label: 'Denominator (Divisor)' }
                          ];
                        } else if (calcType === 'subtract') {
                          return [
                            { value: 'operand1', label: 'Operand 1 (Subtract From)' },
                            { value: 'operand2', label: 'Operand 2 (Subtract)' }
                          ];
                        } else if (calcType === 'multiply' || calcType === 'add') {
                          return [
                            { value: 'operand1', label: 'Operand 1' },
                            { value: 'operand2', label: 'Operand 2' },
                            { value: 'operand3', label: 'Operand 3' },
                            { value: 'operand4', label: 'Operand 4' },
                            { value: 'operand5', label: 'Operand 5' }
                          ];
                        }
                        return [];
                      };
                      
                      // Get available reference configurations
                      const currentCalcDeps = form.getFieldValue('calculation_dependencies') || {};
                      const availableReferences = Object.keys(currentCalcDeps)
                        .filter(key => key.match(/^reference\d+$/))
                        .map(key => ({
                          key,
                          config: currentCalcDeps[key],
                          propertyKeys: currentCalcDeps[key]?.property_keys || (currentCalcDeps[key]?.property_key ? [currentCalcDeps[key].property_key] : [])
                        }));
                      
                      // Get property keys for selected reference
                      const selectedRefKey = sourceProp.reference;
                      const selectedRef = availableReferences.find(r => r.key === selectedRefKey);
                      const availablePropertyKeys = selectedRef?.propertyKeys || [];
                      
                      return (
                        <Card key={index} size="small" style={{ marginBottom: 8 }}>
                          <Row gutter={16}>
                            <Col span={6}>
                              <Form.Item label="Operator">
                                <Select
                                  value={sourceProp.operator || ''}
                                  onChange={(value) => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = [...currentSourceProps];
                                    updatedSourceProps[index] = {
                                      ...updatedSourceProps[index],
                                      operator: value
                                    };
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  placeholder="Select operator"
                                  style={{ width: '100%' }}
                                >
                                  {getOperatorOptions().map(opt => (
                                    <Option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item label="Reference Configuration">
                                <Select
                                  value={sourceProp.reference || ''}
                                  onChange={(value) => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = [...currentSourceProps];
                                    updatedSourceProps[index] = {
                                      ...updatedSourceProps[index],
                                      reference: value,
                                      property_index: 0, // Reset to first property_key
                                      property_key: undefined // Clear direct property_key
                                    };
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  placeholder={availableReferences.length === 0 ? "No reference configurations - create one in References tab" : "Select reference"}
                                  style={{ width: '100%' }}
                                  showSearch
                                  allowClear
                                  notFoundContent={availableReferences.length === 0 ? "No reference configurations available. Create one in the References tab with property keys." : "No matching reference"}
                                >
                                  {availableReferences.map(ref => (
                                    <Option key={ref.key} value={ref.key}>
                                      {ref.key} ({ref.propertyKeys.length} property{ref.propertyKeys.length !== 1 ? 'ies' : 'y'})
                                    </Option>
                                  ))}
                                </Select>
                                {availableReferences.length === 0 && (
                                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                                    Go to the References tab to create a reference configuration with property keys
                                  </Text>
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item label="Property Key">
                                <Select
                                  value={sourceProp.property_index !== undefined ? sourceProp.property_index : sourceProp.property_key || ''}
                                  onChange={(value) => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = [...currentSourceProps];
                                    updatedSourceProps[index] = {
                                      ...updatedSourceProps[index],
                                      property_index: typeof value === 'number' ? value : undefined,
                                      property_key: typeof value === 'string' ? value : undefined
                                    };
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  placeholder={selectedRefKey ? "Select property key" : "Select reference first"}
                                  style={{ width: '100%' }}
                                  disabled={!selectedRefKey || availablePropertyKeys.length === 0}
                                >
                                  {availablePropertyKeys.map((propKey: string, idx: number) => (
                                    <Option key={idx} value={idx}>
                                      {propKey} (index {idx})
                                    </Option>
                                  ))}
                                </Select>
                                {!selectedRefKey && (
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Create a reference configuration in the References tab with property_keys
                                  </Text>
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <Form.Item label=" ">
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = currentSourceProps.filter((_: any, i: number) => i !== index);
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  title="Remove operand"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      );
                    })}
                    <Button
                      type="dashed"
                      onClick={() => {
                        const currentSourceProps = calculation.config?.source_properties || [];
                        const calcType = calculation.config?.calculation_type;
                        let defaultOperator = 'operand1';
                        if (calcType === 'divide') {
                          defaultOperator = currentSourceProps.length === 0 ? 'numerator' : 'denominator';
                        } else if (calcType === 'subtract') {
                          defaultOperator = currentSourceProps.length === 0 ? 'operand1' : 'operand2';
                        } else {
                          defaultOperator = `operand${currentSourceProps.length + 1}`;
                        }
                        const updatedSourceProps = [
                          ...currentSourceProps,
                          { operator: defaultOperator, reference: '', property_index: 0 }
                        ];
                        handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                      }}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: 8 }}
                    >
                      Add Operand
                    </Button>
                  </div>
                </>
              )}

              {/* Average, Min, Max calculation configuration */}
              {['avg', 'min', 'max'].includes(calculation.config?.calculation_type || '') && (
                <>
                  <Divider orientation="left">
                    {calculation.config?.calculation_type === 'avg' && 'Average Configuration'}
                    {calculation.config?.calculation_type === 'min' && 'Minimum Configuration'}
                    {calculation.config?.calculation_type === 'max' && 'Maximum Configuration'}
                  </Divider>
                  <Alert
                    message="Reference Configuration Required"
                    description={
                      calculation.config?.calculation_type === 'avg' 
                        ? "Select property keys from reference configurations to average. Each property key will be included in the average calculation."
                        : calculation.config?.calculation_type === 'min'
                        ? "Select property keys from reference configurations to find the minimum. Each property key will be included in the minimum calculation."
                        : "Select property keys from reference configurations to find the maximum. Each property key will be included in the maximum calculation."
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <div>
                    {(Array.isArray(calculation.config?.source_properties) ? calculation.config.source_properties : []).map((sourceProp: any, index: number) => {
                      // Get available reference configurations
                      const currentCalcDeps = form.getFieldValue('calculation_dependencies') || {};
                      const availableReferences = Object.keys(currentCalcDeps)
                        .filter(key => key.match(/^reference\d+$/))
                        .map(key => ({
                          key,
                          config: currentCalcDeps[key],
                          propertyKeys: currentCalcDeps[key]?.property_keys || (currentCalcDeps[key]?.property_key ? [currentCalcDeps[key].property_key] : [])
                        }));
                      
                      // Get property keys for selected reference
                      const selectedRefKey = sourceProp.reference;
                      const selectedRef = availableReferences.find(r => r.key === selectedRefKey);
                      const availablePropertyKeys = selectedRef?.propertyKeys || [];
                      
                      return (
                        <Card key={index} size="small" style={{ marginBottom: 8 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item label="Reference Configuration">
                                <Select
                                  value={sourceProp.reference || ''}
                                  onChange={(value) => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = [...currentSourceProps];
                                    updatedSourceProps[index] = {
                                      ...updatedSourceProps[index],
                                      reference: value,
                                      property_index: 0, // Reset to first property_key
                                      property_key: undefined // Clear direct property_key
                                    };
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  placeholder={availableReferences.length === 0 ? "No reference configurations - create one in References tab" : "Select reference"}
                                  style={{ width: '100%' }}
                                  showSearch
                                  allowClear
                                  notFoundContent={availableReferences.length === 0 ? "No reference configurations available. Create one in the References tab with property keys." : "No matching reference"}
                                >
                                  {availableReferences.map(ref => (
                                    <Option key={ref.key} value={ref.key}>
                                      {ref.key} ({ref.propertyKeys.length} property{ref.propertyKeys.length !== 1 ? 'ies' : 'y'})
                                    </Option>
                                  ))}
                                </Select>
                                {availableReferences.length === 0 && (
                                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                                    Go to the References tab to create a reference configuration with property keys
                                  </Text>
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={10}>
                              <Form.Item label="Property Key">
                                <Select
                                  value={sourceProp.property_index !== undefined ? sourceProp.property_index : sourceProp.property_key || ''}
                                  onChange={(value) => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = [...currentSourceProps];
                                    updatedSourceProps[index] = {
                                      ...updatedSourceProps[index],
                                      property_index: typeof value === 'number' ? value : undefined,
                                      property_key: typeof value === 'string' ? value : undefined
                                    };
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  placeholder={selectedRefKey ? "Select property key" : "Select reference first"}
                                  style={{ width: '100%' }}
                                  disabled={!selectedRefKey || availablePropertyKeys.length === 0}
                                >
                                  {availablePropertyKeys.map((propKey: string, idx: number) => (
                                    <Option key={idx} value={idx}>
                                      {propKey} (index {idx})
                                    </Option>
                                  ))}
                                </Select>
                                {!selectedRefKey && (
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Create a reference configuration in the References tab with property_keys
                                  </Text>
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <Form.Item label=" ">
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    const currentSourceProps = calculation.config?.source_properties || [];
                                    const updatedSourceProps = currentSourceProps.filter((_: any, i: number) => i !== index);
                                    handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                                  }}
                                  title="Remove property key"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      );
                    })}
                    <Button
                      type="dashed"
                      onClick={() => {
                        const currentSourceProps = calculation.config?.source_properties || [];
                        const updatedSourceProps = [
                          ...currentSourceProps,
                          { reference: '', property_index: 0 }
                        ];
                        handleCalculationChange(calculation.key, 'source_properties', updatedSourceProps);
                      }}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: 8 }}
                    >
                      Add Property Key
                    </Button>
                  </div>
                </>
              )}

              {/* Sum calculation filter configuration */}
              {calculation.config?.calculation_type === 'sum' && (
                <>
                  <Divider orientation="left">Sum Configuration</Divider>
                  
                  <Form.Item label="Property Key to Sum">
                    <PropertyKeySelector
                      value={calculation.config?.calculation_config?.sum_config?.property_key || ''}
                      onChange={(value) => {
                        const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                        const currentCalculationConfig = calculation.config?.calculation_config || {};
                        handleCalculationChange(calculation.key, 'calculation_config', {
                          ...currentCalculationConfig,
                          sum_config: {
                            ...currentConfig,
                            property_key: value
                          }
                        });
                      }}
                      placeholder="Select property key to sum up"
                    />
                  </Form.Item>

                  <Form.Item label="Artifact Filter">
                    <Select
                      value={calculation.config?.calculation_config?.sum_config?.artifact_filter || 'all'}
                      onChange={(value) => {
                        const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                        const currentCalculationConfig = calculation.config?.calculation_config || {};
                        handleCalculationChange(calculation.key, 'calculation_config', {
                          ...currentCalculationConfig,
                          sum_config: {
                            ...currentConfig,
                            artifact_filter: value
                          }
                        });
                      }}
                      placeholder="Select artifact filter"
                      style={{ width: '100%' }}
                    >
                      <Option value="all">All (any artifact_item with that property_key)</Option>
                      <Option value="child">Child (only children artifacts of the current artifact)</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item label="Date Range Filter">
                    <Select
                      value={calculation.config?.calculation_config?.sum_config?.date_range_filter || undefined}
                      onChange={(value) => {
                        const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                        const currentCalculationConfig = calculation.config?.calculation_config || {};
                        handleCalculationChange(calculation.key, 'calculation_config', {
                          ...currentCalculationConfig,
                          sum_config: {
                            ...currentConfig,
                            date_range_filter: value
                          }
                        });
                      }}
                      placeholder="Select date range filter (optional)"
                      style={{ width: '100%' }}
                      allowClear
                    >
                      <Option value="reporting_period">Reporting Period</Option>
                      <Option value="user_input">User Input</Option>
                      <Option value="defined">Defined</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item label="Property Key Filters">
                    <div>
                      {(calculation.config?.calculation_config?.sum_config?.property_filters || []).map((filter: any, index: number) => (
                        <Card key={index} size="small" style={{ marginBottom: 8 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <PropertyKeySelector
                                value={filter.property_key || ''}
                                onChange={(value) => {
                                  const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                                  const currentFilters = currentConfig.property_filters || [];
                                  const updatedFilters = [...currentFilters];
                                  updatedFilters[index] = {
                                    ...updatedFilters[index],
                                    property_key: value
                                  };
                                  handleCalculationChange(calculation.key, 'calculation_config', {
                                    ...(calculation.config?.calculation_config || {}),
                                    sum_config: {
                                      ...currentConfig,
                                      property_filters: updatedFilters
                                    }
                                  });
                                }}
                                placeholder="Property key"
                              />
                            </Col>
                            <Col span={10}>
                              <Input
                                value={filter.value || ''}
                                onChange={(e) => {
                                  const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                                  const currentFilters = currentConfig.property_filters || [];
                                  const updatedFilters = [...currentFilters];
                                  updatedFilters[index] = {
                                    ...updatedFilters[index],
                                    value: e.target.value
                                  };
                                  handleCalculationChange(calculation.key, 'calculation_config', {
                                    ...(calculation.config?.calculation_config || {}),
                                    sum_config: {
                                      ...currentConfig,
                                      property_filters: updatedFilters
                                    }
                                  });
                                }}
                                placeholder="Filter value"
                              />
                            </Col>
                            <Col span={4}>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                                  const currentFilters = currentConfig.property_filters || [];
                                  const updatedFilters = currentFilters.filter((_: any, i: number) => i !== index);
                                  handleCalculationChange(calculation.key, 'calculation_config', {
                                    ...(calculation.config?.calculation_config || {}),
                                    sum_config: {
                                      ...currentConfig,
                                      property_filters: updatedFilters
                                    }
                                  });
                                }}
                                title="Remove filter"
                              />
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() => {
                          const currentConfig = calculation.config?.calculation_config?.sum_config || {};
                          const currentFilters = currentConfig.property_filters || [];
                          handleCalculationChange(calculation.key, 'calculation_config', {
                            ...(calculation.config?.calculation_config || {}),
                            sum_config: {
                              ...currentConfig,
                              property_filters: [...currentFilters, { property_key: '', value: '' }]
                            }
                          });
                        }}
                        icon={<PlusOutlined />}
                        block
                        style={{ marginTop: 8 }}
                      >
                        Add Property Key Filter
                      </Button>
                    </div>
                  </Form.Item>
                </>
              )}

              <Form.Item label="Source Properties (JSON)">
                <TextArea
                  rows={3}
                  value={JSON.stringify(calculation.config?.source_properties || [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleCalculationChange(calculation.key, 'source_properties', parsed);
                    } catch (err) {
                      // Invalid JSON, ignore for now
                    }
                  }}
                  placeholder='[{"operator": "operand1", "property_key": "prop1"}]'
                />
              </Form.Item>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// Condition Tab Content Component for managing named condition configurations
const ConditionTabContent: React.FC<ConditionTabContentProps> = ({ form, validationRules }) => {
  // Get existing condition configurations from validation_rules
  const conditionConfigs = React.useMemo(() => {
    if (!validationRules || typeof validationRules !== 'object') return [];
    const conditions: Array<{ key: string; config: any }> = [];
    Object.keys(validationRules).forEach(key => {
      if (key.match(/^condition\d+$/)) {
        conditions.push({ key, config: validationRules[key] });
      }
    });
    return conditions.sort((a, b) => {
      const numA = parseInt(a.key.match(/\d+$/)?.[0] || '0', 10);
      const numB = parseInt(b.key.match(/\d+$/)?.[0] || '0', 10);
      return numA - numB;
    });
  }, [validationRules]);

  const [localConditions, setLocalConditions] = useState<Array<{ key: string; config: any }>>(conditionConfigs);

  React.useEffect(() => {
    setLocalConditions(conditionConfigs);
  }, [conditionConfigs]);

  // Sync with form
  React.useEffect(() => {
    const currentFormValidationRules = form.getFieldValue('validation_rules') || {};
    const conditionKeys = Object.keys(validationRules || {}).filter(key => key.match(/^condition\d+$/));
    
    if (conditionKeys.length > 0) {
      const updates: Record<string, any> = {};
      conditionKeys.forEach(key => {
        if (validationRules && validationRules[key]) {
          updates[key] = validationRules[key];
        }
      });
      
      form.setFieldsValue({
        validation_rules: {
          ...currentFormValidationRules,
          ...updates
        }
      });
    }
  }, [validationRules, form]);

  const handleAddCondition = () => {
    const existingNumbers = localConditions.map(c => parseInt(c.key.match(/\d+$/)?.[0] || '0', 10));
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    const newKey = `condition${nextNumber}`;
    const newCondition = {
      key: newKey,
      config: {
        when: '',
        required_if: ''
      }
    };
    const updated = [...localConditions, newCondition];
    setLocalConditions(updated);
    
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    updated.forEach(condition => {
      updatedValidationRules[condition.key] = condition.config;
    });
    form.setFieldsValue({ validation_rules: updatedValidationRules });
  };

  const handleDeleteCondition = (conditionKey: string) => {
    const updated = localConditions.filter(c => c.key !== conditionKey);
    setLocalConditions(updated);
    
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    delete updatedValidationRules[conditionKey];
    updated.forEach(condition => {
      updatedValidationRules[condition.key] = condition.config;
    });
    form.setFieldsValue({ validation_rules: updatedValidationRules });
  };

  const handleConditionChange = (conditionKey: string, field: string, value: any) => {
    const updated = localConditions.map(c => {
      if (c.key === conditionKey) {
        return {
          ...c,
          config: {
            ...c.config,
            [field]: value
          }
        };
      }
      return c;
    });
    setLocalConditions(updated);
    
    const currentValidationRules = form.getFieldValue('validation_rules') || {};
    const updatedValidationRules: Record<string, any> = { ...currentValidationRules };
    updated.forEach(condition => {
      updatedValidationRules[condition.key] = condition.config;
    });
    form.setFieldsValue({ validation_rules: updatedValidationRules });
  };

  return (
    <>
      <Alert
        message="Named Condition Configurations"
        description="Define reusable condition configurations that can be referenced by change types. Each configuration specifies when conditions and required_if expressions."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Button
        type="dashed"
        onClick={handleAddCondition}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Condition Configuration
      </Button>

      {localConditions.length === 0 ? (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
          No condition configurations defined.
        </Text>
      ) : (
        <div>
          {localConditions.map(condition => (
            <Card
              key={condition.key}
              size="small"
              style={{ marginBottom: 16 }}
              title={condition.key}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteCondition(condition.key)}
                  title="Delete condition configuration"
                />
              }
            >
              <Form.Item label="When Condition">
                <TextArea
                  rows={3}
                  value={condition.config?.when || ''}
                  onChange={(e) => handleConditionChange(condition.key, 'when', e.target.value)}
                  placeholder="e.g., is_improvement = TRUE"
                />
              </Form.Item>
              
              <Form.Item label="Required If">
                <TextArea
                  rows={3}
                  value={condition.config?.required_if || ''}
                  onChange={(e) => handleConditionChange(condition.key, 'required_if', e.target.value)}
                  placeholder="e.g., property_value > 1000"
                />
              </Form.Item>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

interface PropertyDetailsFormProps {
  form: FormInstance;
  loading?: boolean;
  propertyType: string;
  optionCatalogs: Array<{ id: string; name: string }>;
  stateActions?: StateActions;
  localStateActions?: StateActions;
  changeTypeDefinitions: ChangeTypeDefinition[]; // For backward compatibility
  localChangeTypeDefinitions: TransitionChangeReference[]; // Reference-based format
  selectedChangeType: TransitionChangeReference | null;
  calculationDependencies?: CalculationDependencies | Record<string, any>;
  validationRules?: ValidationRules | Record<string, any>;
  improvementPatterns?: Record<string, any>;
  onPropertyTypeChange: (value: string) => void;
  onStateActionsChange: (actions: StateActions | undefined) => void;
  onSaveStateActions: () => void;
  onStateClick: (state: string, flowType: 'operational' | 'improvement') => void;
  onDeleteChangeType: (changeType: TransitionChangeReference, e?: React.MouseEvent) => void;
  onSelectedChangeTypeChange: (changeType: TransitionChangeReference | null) => void;
  onLocalChangeTypeDefinitionsChange: (definitions: TransitionChangeReference[]) => void;
  onCancel: () => void;
  onSubmit: () => void;
  buildStateActionDetails: () => any;
  ensureRequiredConfiguration: (changeType: TransitionChangeReference) => TransitionChangeReference;
}

export const PropertyDetailsForm: React.FC<PropertyDetailsFormProps> = ({
  form,
  loading = false,
  propertyType,
  optionCatalogs,
  stateActions,
  localStateActions,
  changeTypeDefinitions,
  localChangeTypeDefinitions,
  selectedChangeType,
  calculationDependencies,
  validationRules,
  improvementPatterns = {},
  onPropertyTypeChange,
  onStateActionsChange,
  onSaveStateActions,
  onStateClick,
  onDeleteChangeType,
  onSelectedChangeTypeChange,
  onLocalChangeTypeDefinitionsChange,
  onCancel,
  onSubmit,
  buildStateActionDetails,
  ensureRequiredConfiguration
}) => {
  const watchedPropertyType = Form.useWatch('property_type', form) || propertyType;
  const watchedCalculationType = Form.useWatch('calculation_type', form);
  const watchedFunctionName = Form.useWatch('function_name', form);
  const watchedInputSource = Form.useWatch('input_source', form);
  const watchedAllowUserInput = Form.useWatch('allow_user_input', form);
  const watchedCatalogRef = Form.useWatch('global_option_catalog_ref', form);
  const watchedIsGlobal = Form.useWatch('is_global', form);
  const watchedValidationRules = Form.useWatch('validation_rules', form);
  
  const [selectedCatalogValues, setSelectedCatalogValues] = useState<Array<{ value: string; display_label?: string }>>([]);
  const [loadingCatalogValues, setLoadingCatalogValues] = useState(false);
  const [changeTypeForm] = Form.useForm(); // Separate form instance for change type editing
  
  // Get available named configurations - merge props, form state, and any local updates
  const availableConfigs = React.useMemo(() => {
    // Get form values for calculation_dependencies and validation_rules (includes newly created configs)
    const formCalcDeps = form.getFieldsValue(['calculation_dependencies']).calculation_dependencies;
    const formValidationRules = form.getFieldsValue(['validation_rules']).validation_rules;
    
    // Merge: props (loaded from backend) + form state (newly created/edited configs)
    const mergedCalculationDependencies = {
      ...(calculationDependencies && typeof calculationDependencies === 'object' ? calculationDependencies : {}),
      ...(formCalcDeps && typeof formCalcDeps === 'object' ? formCalcDeps : {})
    };
    
    const mergedValidationRules = {
      ...(validationRules && typeof validationRules === 'object' ? validationRules : {}),
      ...(formValidationRules && typeof formValidationRules === 'object' ? formValidationRules : {})
    };
    
    const configs = getAvailableNamedConfigurations(mergedCalculationDependencies, mergedValidationRules);
    console.log('[PropertyDetailsForm] Available named configurations:', {
      user_inputs: configs.user_inputs,
      calculations: configs.calculations,
      references: configs.references,
      conditions: configs.conditions,
      validations: configs.validations,
      resources: configs.resources,
      propsCalcDepsKeys: calculationDependencies ? Object.keys(calculationDependencies) : [],
      formCalcDepsKeys: formCalcDeps ? Object.keys(formCalcDeps) : [],
      mergedCalcDepsKeys: Object.keys(mergedCalculationDependencies),
      propsValidationKeys: validationRules ? Object.keys(validationRules) : [],
      formValidationKeys: formValidationRules ? Object.keys(formValidationRules) : [],
      mergedValidationKeys: Object.keys(mergedValidationRules)
    });
    return configs;
  }, [calculationDependencies, validationRules, form]);
  
  // Update form fields when selectedChangeType changes
  React.useEffect(() => {
    if (selectedChangeType) {
      changeTypeForm.setFieldsValue(selectedChangeType);
    } else {
      changeTypeForm.resetFields();
    }
  }, [selectedChangeType, changeTypeForm]);
  
  // Fetch option catalog values when catalog is selected and property type is 'list'
  useEffect(() => {
    if (watchedPropertyType === 'list' && watchedCatalogRef) {
      setLoadingCatalogValues(true);
      apiClient.get(`/option-catalogs/${watchedCatalogRef}`)
        .then(response => {
          const catalog = response.data;
          const values = catalog.values || [];
          // Sort by sequence_order if available
          const sortedValues = [...values].sort((a: any, b: any) => 
            (a.sequence_order || 0) - (b.sequence_order || 0)
          );
          setSelectedCatalogValues(sortedValues.map((val: any) => ({
            value: val.value,
            display_label: val.display_label || val.value
          })));
        })
        .catch((error: any) => {
          console.error('Failed to fetch option catalog values:', error);
          message.warning('Failed to load catalog values');
          setSelectedCatalogValues([]);
        })
        .finally(() => {
          setLoadingCatalogValues(false);
        });
    } else {
      setSelectedCatalogValues([]);
    }
  }, [watchedPropertyType, watchedCatalogRef]);

  // Auto-check "Allow User Input" when input_source is "user_input"
  useEffect(() => {
    if (watchedInputSource === 'user_input') {
      form.setFieldsValue({ allow_user_input: true });
    }
  }, [watchedInputSource, form]);

  // Auto-configure global property settings
  useEffect(() => {
    if (watchedIsGlobal) {
      // Set input_source to system_generated
      const currentInputSource = form.getFieldValue('input_source');
      if (currentInputSource !== 'system_generated') {
        form.setFieldsValue({ input_source: 'system_generated' });
      }
      
      // Set allow_user_input to false
      form.setFieldsValue({ allow_user_input: false });
      
      // Set read_only in validation_rules
      const currentValidationRules = form.getFieldValue('validation_rules') || {};
      if (!currentValidationRules.read_only) {
        form.setFieldsValue({
          validation_rules: {
            ...currentValidationRules,
            read_only: true
          }
        });
      }
    }
  }, [watchedIsGlobal, form]);

  // State for field selection
  const [fieldsToSave, setFieldsToSave] = useState<string[]>([]); // Empty array means save all

  const tabItems = [
    {
      key: 'basic',
      label: 'Basic Info',
      children: (
        <>
          <Form.Item
            name="property_key"
            label="Property Key"
            rules={[
              { required: true, message: 'Property key is required' },
              { pattern: /^[a-z0-9_]+$/, message: 'Property key must be lowercase alphanumeric with underscores' }
            ]}
          >
            <Input disabled placeholder="e.g., revenue, customer_name" />
          </Form.Item>

          <Form.Item
            name="property_type"
            label="Property Type"
            rules={[{ required: true, message: 'Property type is required' }]}
          >
            <Select onChange={onPropertyTypeChange}>
              {PROPERTY_TYPES.map(type => (
                <Option key={type} value={type}>
                  <Tag>{type}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Describe what this property represents" />
          </Form.Item>

          <Form.Item
            name="is_global"
            label="Global Property (Krysalis-Only)"
            valuePropName="checked"
            tooltip="Mark this as a global property that is set once and used by all artifact_items. This makes it Krysalis-only (not editable by customers). When enabled, the property will use system_generated input source, require a default value, and be read-only."
          >
            <Switch />
          </Form.Item>

          {watchedIsGlobal && (
            <Alert
              message="Global Property Configuration"
              description="This property will be set globally and used by all artifact_items. It will be initialized with the default value you specify and cannot be edited by customers. Make sure to set a default value below and configure state actions to 'System' in the Change Types tab."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Default Value for Global Properties */}
          {watchedIsGlobal && (
            <Form.Item
              name="default_value"
              label="Default Value (Required for Global Properties)"
              rules={[{ required: watchedIsGlobal, message: 'Default value is required for global properties' }]}
              tooltip="This value will be used to initialize this property in all artifact_items. Customers cannot modify this value."
            >
              {watchedPropertyType === 'float' || watchedPropertyType === 'number' || watchedPropertyType === 'integer' ? (
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter default numeric value"
                  precision={watchedPropertyType === 'integer' ? 0 : undefined}
                />
              ) : watchedPropertyType === 'boolean' ? (
                <Select placeholder="Select default boolean value">
                  <Option value={true}>True</Option>
                  <Option value={false}>False</Option>
                </Select>
              ) : watchedPropertyType === 'date' ? (
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select default date"
                />
              ) : (
                <Input placeholder="Enter default value" />
              )}
            </Form.Item>
          )}

          <Form.Item
            name="input_source"
            label="Input Source"
          >
            <Select disabled={watchedIsGlobal}>
              <Option value="user_input">User Input</Option>
              <Option value="system_generated">System Generated</Option>
              <Option value="referenced_artifact">Referenced Artifact</Option>
              <Option value="transformed_reference">Transformed Reference</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="allow_user_input"
            label="Allow User Input"
            valuePropName="checked"
            tooltip="Enable user input fields for this property. Automatically checked if Input Source is 'User Input'. Disabled for global properties."
          >
            <Switch disabled={watchedIsGlobal} />
          </Form.Item>

          {watchedAllowUserInput && (
            <>
              <Divider orientation="left">User Input Configuration</Divider>
              <Form.Item
                name="user_input_type"
                label="User Input Type"
                tooltip="Derived from property type"
                initialValue={watchedPropertyType}
              >
                <Input disabled value={watchedPropertyType} />
              </Form.Item>
              <Form.Item
                name="user_input_label"
                label="User Input Label"
              >
                <Input placeholder="Enter label for user input field" />
              </Form.Item>
              <Form.Item
                name="user_input_help_text"
                label="User Input Help Text"
              >
                <TextArea rows={2} placeholder="Enter help text for user input field" />
              </Form.Item>
              <Form.Item
                name="user_input_default_value"
                label="Default Value"
              >
                {watchedPropertyType === 'list' ? (
                  <Select
                    placeholder={watchedCatalogRef ? "Select default value" : "Select an option catalog first"}
                    disabled={!watchedCatalogRef}
                    loading={loadingCatalogValues}
                    showSearch
                    filterOption={(input, option) => {
                      const label = option?.label || option?.children || '';
                      return String(label).toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {selectedCatalogValues.map((val) => (
                      <Option key={val.value} value={val.value}>
                        {val.display_label || val.value}
                      </Option>
                    ))}
                  </Select>
                ) : isNumericPropertyType(watchedPropertyType) ? (
                  <InputNumber style={{ width: '100%' }} />
                ) : watchedPropertyType === 'boolean' ? (
                  <Select>
                    <Option value={true}>True</Option>
                    <Option value={false}>False</Option>
                  </Select>
                ) : (
                  <Input placeholder="Enter default value" />
                )}
              </Form.Item>
              {watchedPropertyType === 'list' && (
                <Form.Item
                  name="allow_multiple_selections"
                  label="Allow Multiple Selections"
                  valuePropName="checked"
                  tooltip="Allow users to select multiple values from the list"
                >
                  <Switch />
                </Form.Item>
              )}
              {isNumericPropertyType(watchedPropertyType) && (
                <Form.Item
                  name={['user_input', 'display_format']}
                  label="Display Format"
                  tooltip="Select how the value should be displayed to users. 'Percentage' shows 0-100 with % suffix (stored as 0.0-1.0). 'Decimal' shows the actual decimal value."
                >
                  <Select
                    placeholder="Select display format (optional)"
                    allowClear
                  >
                    <Option value="decimal">Decimal (0.0-1.0)</Option>
                    <Option value="percentage">Percentage (0-100%)</Option>
                  </Select>
                </Form.Item>
              )}
              {watchedPropertyType === 'string' && (
                <>
                  <Form.Item
                    name="user_input_mask"
                    label="Input Mask"
                    tooltip="Select an input mask format for the string field"
                  >
                    <Select
                      placeholder="Select input mask (optional)"
                      allowClear
                    >
                      {INPUT_MASK_TYPES.map(mask => (
                        <Option key={mask.value} value={mask.value}>
                          {mask.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => 
                      prevValues.user_input_mask !== currentValues.user_input_mask
                    }
                  >
                    {({ getFieldValue }) => {
                      const maskType = getFieldValue('user_input_mask');
                      if (maskType === 'custom') {
                        return (
                          <Form.Item
                            name="user_input_mask_pattern"
                            label="Custom Mask Pattern"
                            tooltip="Enter a regex pattern for the custom mask"
                            rules={[
                              { required: true, message: 'Custom mask pattern is required when custom mask is selected' }
                            ]}
                          >
                            <Input placeholder="e.g., ^[A-Z]{2}\\d{4}$" />
                          </Form.Item>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>
                </>
              )}
            </>
          )}

          {watchedPropertyType === 'list' && (
            <Form.Item
              name="global_option_catalog_ref"
              label="Option Catalog"
              tooltip="Select an option catalog for list-type properties"
            >
              <Select
                placeholder="Select option catalog"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {optionCatalogs.map(cat => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </>
      )
    },
    {
      key: 'states',
      label: 'States',
      children: (
        <>
          <Form.Item
            label="Configure State Actions"
            tooltip="Select actions for each state in the operational and improvement flows"
          >
            <StateFlowEditor
              key={form.getFieldValue('property_key') || 'no-property'}
              value={localStateActions}
              onChange={onStateActionsChange}
              onStateClick={onStateClick}
              stateActionDetails={buildStateActionDetails()}
              editable={true}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              onClick={onSaveStateActions}
              loading={loading}
              disabled={!form.getFieldValue('property_key')}
            >
              Save State Actions
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                onStateActionsChange(stateActions);
              }}
              disabled={!form.getFieldValue('property_key')}
            >
              Reset
            </Button>
          </Form.Item>
        </>
      )
    },
    {
      key: 'calculations',
      label: 'Calculation',
      children: (
        <>
          <Form.Item name="calculation_dependencies" hidden preserve>
            <Input type="hidden" />
          </Form.Item>
          <CalculationTabContent form={form} propertyType={propertyType} calculationDependencies={calculationDependencies} />
        </>
      )
    },
    {
      key: 'references',
      label: 'References',
      children: (
        <>
          <Form.Item name="calculation_dependencies" hidden preserve>
            <Input type="hidden" />
          </Form.Item>
          <ReferenceTabContent 
            form={form} 
            calculationDependencies={calculationDependencies}
          />
        </>
      )
    },
    {
      key: 'conditions',
      label: 'Conditions',
      children: (
        <>
          <Form.Item name="validation_rules" hidden preserve>
            <Input type="hidden" />
          </Form.Item>
          <ConditionTabContent form={form} validationRules={validationRules} />
        </>
      )
    },
    {
      key: 'resource',
      label: 'Resource',
      children: (
        <>
          {/* Hidden Form.Item to ensure validation_rules is tracked by the form */}
          {/* This ensures validation_rules (with resource configurations) is included in form.getFieldsValue() */}
          <Form.Item 
            name="validation_rules" 
            preserve
            initialValue={{}}
            noStyle
            shouldUpdate
          >
            <div style={{ display: 'none' }} />
          </Form.Item>
          <ResourceTabContent form={form} validationRules={validationRules} />
        </>
      )
    },
    {
      key: 'validation',
      label: 'Validation',
      children: (
        <>
          <Form.Item name="validation_rules" hidden preserve>
            <Input type="hidden" />
          </Form.Item>
          <ValidationTabContent 
            form={form} 
            validationRules={validationRules}
            propertyType={watchedPropertyType}
          />
        </>
      )
    },
    {
      key: 'change-types',
      label: 'Change Types',
      children: (
        <>
          <Row gutter={16}>
            <Col xs={24} lg={10}>
              <Card
                title={
                  <Space>
                    <span>Change Types</span>
                    <Tag>{localChangeTypeDefinitions.length}</Tag>
                  </Space>
                }
                extra={
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const existingNames = localChangeTypeDefinitions.map(ct => ct.change_type_name);
                      let newName = 'NewChangeType';
                      let counter = 1;
                      while (existingNames.includes(newName)) {
                        counter++;
                        newName = `NewChangeType${counter}`;
                      }
                      const newChangeType: TransitionChangeReference = {
                        change_type_name: newName,
                        state_action_type: 'System'
                      };
                      onLocalChangeTypeDefinitionsChange([...localChangeTypeDefinitions, newChangeType]);
                      onSelectedChangeTypeChange(newChangeType);
                    }}
                  >
                    Add
                  </Button>
                }
              >
                {localChangeTypeDefinitions.length === 0 ? (
                  <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '20px 0' }}>
                    No change types defined. Click "Add" to create one.
                  </Text>
                ) : (
                  <Table
                    dataSource={localChangeTypeDefinitions}
                    rowKey={(record) => {
                      // Ensure unique key: combine all identifying fields
                      // Since change_type_name might not be unique, include all distinguishing fields
                      const name = record.change_type_name || 'unnamed';
                      const actionType = record.state_action_type || 'unknown';
                      // Create a unique key by combining all reference fields
                      const refs = [
                        record.user_input,
                        record.calculation,
                        record.reference,
                        record.condition,
                        record.validation,
                        record.resource
                      ].filter(Boolean).join('-');
                      const recordId = `${name}-${actionType}${refs ? `-${refs}` : ''}`;
                      return recordId;
                    }}
                    size="small"
                    pagination={false}
                    onRow={(record) => ({
                      onClick: () => onSelectedChangeTypeChange(record),
                      style: {
                        cursor: 'pointer',
                        backgroundColor: selectedChangeType?.change_type_name === record.change_type_name ? '#e6f7ff' : 'transparent'
                      }
                    })}
                    columns={[
                      {
                        title: 'Name',
                        key: 'name',
                        width: 150,
                        render: (_: any, record: TransitionChangeReference) => {
                          return <Text strong={selectedChangeType?.change_type_name === record.change_type_name}>{record.change_type_name}</Text>;
                        }
                      },
                      {
                        title: 'State Action Type',
                        key: 'state_action_type',
                        width: 150,
                        render: (_: any, record: TransitionChangeReference, index: number) => {
                          return <Tag key={`${record.change_type_name}-${index}-tag`} color="blue">{record.state_action_type}</Tag>;
                        }
                      },
                      {
                        title: 'Config References',
                        key: 'references',
                        render: (_: any, record: TransitionChangeReference) => {
                          const refs: string[] = [];
                          if (record.user_input) refs.push(`UI: ${record.user_input}`);
                          if (record.calculation) refs.push(`Calc: ${record.calculation}`);
                          if (record.reference) refs.push(`Ref: ${record.reference}`);
                          if (record.condition) refs.push(`Cond: ${record.condition}`);
                          if (record.validation) refs.push(`Val: ${record.validation}`);
                          if (record.resource) refs.push(`Res: ${record.resource}`);
                          return <Text type="secondary" style={{ fontSize: '12px' }}>{refs.join(', ') || 'None'}</Text>;
                        }
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        width: 80,
                        render: (_: any, record: TransitionChangeReference) => (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteChangeType(record, e);
                            }}
                            title="Delete change type"
                          />
                        )
                      }
                    ]}
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card title={selectedChangeType ? `Edit Change Type: ${selectedChangeType.change_type_name}` : 'Select a Change Type'}>
                {selectedChangeType ? (
                  <Form
                    form={changeTypeForm}
                    component={false}
                    layout="vertical"
                    initialValues={selectedChangeType}
              onValuesChange={(changedValues, allValues) => {
                // Find by the current selectedChangeType's name (before potential change)
                const currentSelectedName = selectedChangeType.change_type_name;
                const index = localChangeTypeDefinitions.findIndex(ct => ct.change_type_name === currentSelectedName);
                if (index !== -1) {
                  const updated = [...localChangeTypeDefinitions];
                  // Create updated object, preserving existing values and merging new ones
                  updated[index] = {
                    ...localChangeTypeDefinitions[index],
                    ...allValues
                  };
                  
                  // If state_action_type changed, clear incompatible fields and auto-generate required configuration
                  if (changedValues.state_action_type) {
                    const newStateActionType = changedValues.state_action_type;
                    // Clear fields that are not applicable to the new state_action_type
                    if (newStateActionType !== 'User') {
                      delete updated[index].user_input;
                    }
                    if (newStateActionType !== 'Calculate' && newStateActionType !== 'System') {
                      delete updated[index].calculation;
                    }
                    if (newStateActionType !== 'Reference') {
                      delete updated[index].reference;
                    }
                    
                    // Auto-generate required configuration if it doesn't exist
                    const changeTypeWithConfig = ensureRequiredConfiguration(updated[index]);
                    updated[index] = changeTypeWithConfig;
                    
                    // Update form fields to reflect the auto-generated configuration
                    changeTypeForm.setFieldsValue({
                      ...changeTypeForm.getFieldsValue(),
                      ...changeTypeWithConfig
                    });
                    
                    // Trigger validation after clearing fields
                    setTimeout(() => {
                      changeTypeForm.validateFields(['user_input', 'calculation', 'reference']).catch(() => {
                        // Validation errors are expected if fields are required but empty
                      });
                    }, 0);
                  }
                  
                  onLocalChangeTypeDefinitionsChange(updated);
                  // Update selected change type to reflect changes (especially if name changed)
                  onSelectedChangeTypeChange(updated[index]);
                }
              }}
                  >
                    <Form.Item
                      name="change_type_name"
                      label="Change Type Name"
                      rules={[
                        { required: true, message: 'Change type name is required' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const duplicates = localChangeTypeDefinitions.filter(
                              ct => ct.change_type_name === value && ct.change_type_name !== selectedChangeType.change_type_name
                            );
                            if (duplicates.length > 0) {
                              return Promise.reject(new Error('Change type name must be unique'));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input placeholder="Enter change type name" />
                    </Form.Item>

                    <Form.Item
                      name="state_action_type"
                      label="State Action Type"
                      rules={[{ required: true, message: 'State action type is required' }]}
                    >
                      <Select placeholder="Select state action type">
                        <Option value="System">System</Option>
                        <Option value="User">User</Option>
                        <Option value="Calculate">Calculate</Option>
                        <Option value="Reference">Reference</Option>
                      </Select>
                    </Form.Item>

                    <Divider>Configuration References</Divider>
                    <Alert
                      message="Select Configuration References"
                      description="Choose which named configurations this change type uses. Edit the actual configuration content in their respective tabs."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const stateActionType = changeTypeForm.getFieldValue('state_action_type') || selectedChangeType?.state_action_type;
                        return (
                          <>
                            {(stateActionType === 'User') && (
                              <Form.Item
                                name="user_input"
                                label="User Input Configuration"
                                tooltip="Reference to a named user_input configuration (e.g., user_input1). Required for User change types."
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      const stateActionType = changeTypeForm.getFieldValue('state_action_type');
                                      if (stateActionType === 'User' && !value) {
                                        return Promise.reject(new Error('User Input Configuration is required for User change types'));
                                      }
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                              >
                                <Select
                                  placeholder="Select user input configuration"
                                  allowClear
                                  showSearch
                                >
                                  {availableConfigs.user_inputs.map(configName => (
                                    <Option key={configName} value={configName}>{configName}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            )}

                            {(stateActionType === 'System' || stateActionType === 'Calculate') && (
                              <Form.Item
                                name="calculation"
                                label="Calculation Configuration"
                                tooltip="Reference to a named calculation configuration (e.g., calculation1). Required for System and Calculate change types."
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      const stateActionType = changeTypeForm.getFieldValue('state_action_type');
                                      if ((stateActionType === 'System' || stateActionType === 'Calculate') && !value) {
                                        return Promise.reject(new Error('Calculation Configuration is required for System and Calculate change types'));
                                      }
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                              >
                                <Select
                                  placeholder="Select calculation configuration"
                                  allowClear
                                  showSearch
                                >
                                  {availableConfigs.calculations.map(configName => (
                                    <Option key={configName} value={configName}>{configName}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            )}

                            {stateActionType === 'Reference' && (
                              <Form.Item
                                name="reference"
                                label="Reference Configuration"
                                tooltip="Reference to a named reference configuration (e.g., reference1). Required for Reference change types."
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      const stateActionType = changeTypeForm.getFieldValue('state_action_type');
                                      if (stateActionType === 'Reference' && !value) {
                                        return Promise.reject(new Error('Reference Configuration is required for Reference change types'));
                                      }
                                      return Promise.resolve();
                                    }
                                  }
                                ]}
                              >
                                <Select
                                  placeholder="Select reference configuration"
                                  allowClear
                                  showSearch
                                >
                                  {availableConfigs.references.map(configName => (
                                    <Option key={configName} value={configName}>{configName}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            )}
                          </>
                        );
                      }}
                    </Form.Item>

                    <Form.Item
                      name="condition"
                      label="Condition Configuration"
                      tooltip="Reference to a named condition configuration (e.g., condition1). Optional for all change types."
                    >
                      <Select
                        placeholder="Select condition configuration (optional)"
                        allowClear
                        showSearch
                      >
                        {availableConfigs.conditions.map(configName => (
                          <Option key={configName} value={configName}>{configName}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="validation"
                      label="Validation Configuration"
                      tooltip="Reference to a named validation configuration (e.g., validation1). Optional for all change types."
                    >
                      <Select
                        placeholder="Select validation configuration (optional)"
                        allowClear
                        showSearch
                      >
                        {availableConfigs.validations.map(configName => (
                          <Option key={configName} value={configName}>{configName}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="resource"
                      label="Resource Configuration"
                      tooltip="Reference to a named resource configuration (e.g., resource1). Optional for all change types."
                    >
                      <Select
                        placeholder="Select resource configuration (optional)"
                        allowClear
                        showSearch
                      >
                        {availableConfigs.resources.map(configName => (
                          <Option key={configName} value={configName}>{configName}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Divider />
                    <Space>
                      <Button 
                        danger 
                        onClick={() => {
                          if (selectedChangeType) {
                            const newDefinitions = localChangeTypeDefinitions.filter(
                              ct => ct.change_type_name !== selectedChangeType.change_type_name
                            );
                            onLocalChangeTypeDefinitionsChange(newDefinitions);
                            onSelectedChangeTypeChange(null);
                            void message.success(`Change type "${selectedChangeType.change_type_name}" removed`);
                          }
                        }}
                      >
                        Delete Change Type
                      </Button>
                    </Space>
                  </Form>
                ) : (
                  <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '40px 0' }}>
                    Select a change type from the list to view and edit details
                  </Text>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )
    }
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
    >
      <Tabs items={tabItems} />
      
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Select
          mode="multiple"
          placeholder="Save all fields"
          value={fieldsToSave.length > 0 ? fieldsToSave : undefined}
          onChange={(value) => setFieldsToSave(value)}
          style={{ minWidth: 200 }}
          allowClear
        >
          <Option value="calculation_dependencies">Calculation Dependencies</Option>
          <Option value="validation_rules">Validation Rules</Option>
          <Option value="transition_changes">Transition Changes</Option>
          <Option value="state_actions">State Actions</Option>
        </Select>
        <Button 
          type="primary" 
          htmlType="submit" 
          loading={loading}
          onClick={(e) => {
            // Pass fieldsToSave to the submit handler
            if (fieldsToSave.length > 0) {
              e.preventDefault();
              e.stopPropagation();
              // We need to call onSubmit with the fields
              // Since onFinish doesn't accept parameters, we'll store it in form data
              form.setFieldsValue({ _fieldsToSave: fieldsToSave });
              form.submit();
            }
          }}
        >
          Save{fieldsToSave.length > 0 ? ` (${fieldsToSave.length} selected)` : ''}
        </Button>
      </Space>
    </Form>
  );
};
